import React, { useEffect, useRef, useState } from 'react';
import { Grid, Typography, Button, Collapse, Paper, Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Checkbox, FormControlLabel, FormGroup, CircularProgress, Box } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useHistory } from 'react-router-dom';
import CheckIcon from '@material-ui/icons/Check';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import BusinessIcon from '@material-ui/icons/Business';
import ContactMailIcon from '@material-ui/icons/ContactMail';
import PaymentIcon from '@material-ui/icons/Payment';
import PeopleIcon from '@material-ui/icons/People';
import LanguageIcon from '@material-ui/icons/Language';
import FitnessCenterIcon from '@material-ui/icons/FitnessCenter';
import TuneIcon from '@material-ui/icons/Tune';
import AssessmentIcon from '@material-ui/icons/Assessment';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import DescriptionIcon from '@material-ui/icons/Description';
import AddIcon from '@material-ui/icons/Add';
import MicIcon from '@material-ui/icons/Mic';
import EventIcon from '@material-ui/icons/Event';
import VideocamIcon from '@material-ui/icons/Videocam';
import ChatIcon from '@material-ui/icons/Chat';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Portfolio1 from '../images/portfolio1.png';
import Portfolio2 from '../images/portfolio2.png';
import Portfolio3 from '../images/portfolio3.png';
import Portfolio4 from '../images/portfolio4.png';
import Portfolio5 from '../images/portfolio5.png';
import mobileapp2 from '../images/mobileapp2.png';
import { withStyles } from '@material-ui/core/styles';
import showcaseLaptop from '../images/Copilot_20251021_151902_v2.png';
import CEMark from '../images/CE_mark.png';
import GDPRcompliant_logoGrey from '../images/GDPR-compliant-logo-Grey.png';
import DanishHealth from '../images/Danish_Health_Authority.png';
import { LANDING_LAPTOP_BAND, LANDING_SHARED_BUTTONS, LANDING_SHARED_LAYOUT, LANDING_SHARED_TYPOGRAPHY } from './sharedStyles';

let stripePromise;

const getStripePromise = () => {
    if (!stripePromise) {
        stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_default');
    }
    return stripePromise;
};

const faqs = [
    {
        q: 'How do I get started with VIRTUS™ Clinic?',
        a: "Simply contact us for a demo and onboarding. We'll help you set up your clinic, invite your team, and configure your first intake workflow. Patients can start completing digital intakes the same day."
    },
    {
        q: 'Is VIRTUS™ Clinic compliant with healthcare regulations?',
        a: 'Yes. VIRTUS™ Clinic is CE-approved, fully GDPR compliant, and aligned with the EU AI-Act. Data is encrypted and stored securely, and MitID-ready patient identity flows remain part of the platform roadmap.'
    },
    {
        q: 'Can I use VIRTUS™ Clinic for both in-person and remote care?',
        a: 'Absolutely. VIRTUS™ Clinic is built for hybrid care. Patients can complete intake at home or in your waiting room, attend consultations in-clinic or by video, and follow their exercise program remotely in one workflow.'
    },
    {
        q: 'How does the auto-journaling work?',
        a: 'Patients complete a structured digital intake and VIRTUS™ organises those responses into a structured journal draft. Every later clinical action, including diagnosis selection, exercise adjustments, and advice delivery, updates the journal automatically for clinician review and signature.'
    },
    {
        q: 'Do I need to switch away from my current EHR?',
        a: 'No. Stage 1 works as a digital workflow and patient-management layer alongside your existing EHR. Journal notes can be copied into your current system, while Stage 2 expands into a full clinic operating system when you are ready.'
    },
    {
        q: 'What is the ambient scribe add-on?',
        a: 'Ambient scribe support is positioned as an optional add-on for clinics that want real-time structured transcription during consultations. It complements the VIRTUS™ workflow rather than replacing clinician review.'
    },
];

const SIGNUP_PLANS = [
    {
        id: 'stage0',
        stage: 'Stage 0 · Free Entry',
        tagline: 'Match + Digital Intake',
        price: 'DKK 49',
        priceUnit: '/ per patient lead',
        badge: null,
        accentColor: '#4CAF50',
        features: [
            'Branded intake page + booking URL',
            'Digital intake',
            'Red flag screening',
            'Auto-drafted intake journal note',
            'Patient Dashboard',
            'Works alongside any EHR',
            'No monthly commitment',
            'You sign up - we generate leads',
        ],
    },
    {
        id: 'stage1',
        stage: 'Stage 1 · Subscription',
        tagline: 'Clinical Workflow',
        price: 'DKK 300–400',
        priceUnit: '/ seat / mo',
        badge: '★ THE CORE',
        accentColor: '#FF7F27',
        features: [
            'Branded intake page + booking URL',
            'Digital intake',
            'Red flag screening',
            'Auto-drafted intake journal note',
            'Diagnosis-linked templates and workflows',
            'KPI and feedback based auto follow-up journal note',
            'Action-triggered auto-journaling',
            'Clinician-controlled adaptive exercise programs',
            'Remote monitoring + follow-up insights',
            'Integrated video consultations',
            'Free Patient app',
            'Home exercise delivery',
            'Patient education',
            'Remote monitoring',
            'Secure chat',
            'Calendar & Booking',
            'Billing & Bookkeeping',
            'Stripe payments, subscriptions and auto-pay',
        ],
    },
    {
        id: 'stage2',
        stage: 'Stage 2 · Full Platform',
        tagline: 'Complete Clinic OS',
        price: 'DKK 500–600',
        priceUnit: '/ seat / mo',
        badge: 'THE DESTINATION',
        accentColor: '#002D55',
        features: [
            'Everything in Stage 0+1',
            'Staff admin',
            'Analytics dashboard',
            'EAN billing to insurances',
            'Automated accounting and bookkeeping via Appelsin integration',
            'MedCom epicrisis to GPs',
            'MitID patient login',
            'White-label option',
            'Optional Corti ambient scribe add-on',
        ],
    },
];

const SHOWCASE_VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/media/clinic-demo.mp4`;

const HERO_PROOF_POINTS = [
    'Import existing patient data',
    'Comprehensive clinic management',
    'GDPR and EU AI-Act aligned',
    'Data-encrypted and secure',
];

const PLATFORM_WORKFLOW_STEPS = [
    {
        stage: 'Pre-visit',
        emoji: '🧠',
        title: 'Pre-visit digital intake',
        body: 'Patients share symptoms, history, and movement data before the appointment.',
        outcome: '80-100% journal pre-written',
            outcome: '80-100% journal',
            outcomeHighlight: 'pre-written',
        detail: 'Structured before the first consultation',
        featured: true,
    },
    {
        stage: 'Booking',
        emoji: '🎯',
        title: 'Smart patient matching',
        body: 'Match patients to the right clinician, then automate booking, scheduling, and reminders.',
        outcome: 'Patients match the right clinician',
            outcome: 'Patients match',
            outcomeHighlight: 'the right clinician',
        detail: 'A clearer path from discovery to booking',
    },
    {
        stage: 'Consultations',
        emoji: '🩺',
        title: 'Automated clinical workflows',
        body: 'Review a prepared journal, adjust the plan, and keep clinical actions documented.',
        outcome: 'No cold start. More time for care',
            outcome: 'No cold start. ',
            outcomeHighlight: 'More time for care',
        detail: 'Save time at the every consultation',
    },
    {
        stage: 'Between visits',
        image: mobileapp2,
        title: 'Adaptive plans and monitoring',
        body: 'Patient app delivers plans & tracks symptom response. Data- and action driven auto-journaling write the follow-ups.',
        outcome: 'Data-driven follow-up',
            outcome: 'Data driven ',
            outcomeHighlight: 'automations',
        detail: 'Less admin at every follow-up',
    },
    {
        stage: 'Business operations',
        emoji: '⚙️',
        title: 'Clinic admin → Accounting',
        body: 'Billing, insurance, analytics, reports, and accounting included and supported in automated workflows.',
        outcome: 'Up to 66% lower tooling cost',
            outcome: 'Less admin and lower tooling ',
            outcomeHighlight: 'cost.',
        detail: 'Replace multiple subscriptions with one platform',
    },
];
const REPLACED_TOOLS = [
    { id: 'clinic_system', label: 'Clinic system / EHR', icon: BusinessIcon, range: 'DKK 750-1750', defaultCost: 750 },
    { id: 'exercise_builder', label: 'Home exercise program builder', icon: FitnessCenterIcon, range: 'DKK 200-600', defaultCost: 200 },
    { id: 'ai_scribe', label: 'AI scribe', icon: MicIcon, range: 'DKK 300-900', defaultCost: 300 },
    { id: 'voice_agent', label: 'AI voice agent / Secretary', icon: PhoneIphoneIcon, range: 'DKK 700-1000', defaultCost: 700 },
    { id: 'video_consultations', label: 'Video consultations', icon: VideocamIcon, range: 'DKK 400', defaultCost: 400 },
    { id: 'accounting', label: 'Accounting', icon: ReceiptIcon, range: 'DKK 400-800', defaultCost: 400 },
    { id: 'other', label: 'Other (add custom)', icon: TuneIcon, range: '', defaultCost: 0 },
];

const getDefaultToolSelections = () => REPLACED_TOOLS.reduce((acc, tool) => {
    acc[tool.id] = { enabled: tool.defaultCost > 0 || tool.id === 'other', label: tool.label, cost: String(tool.defaultCost) };
    return acc;
}, {});

const SAVINGS_CALCULATOR_TIERS = [
    {
        id: 'tier2',
        label: 'Tier 2 · Clinical Workflow',
        stage: 'Stage 1',
        estimate: 350,
        range: 'DKK 300-400 / seat / mo',
        summary: 'Digital intake, smart auto-journaling, and clinician-controlled workflow tools.',
    },
    {
        id: 'tier3',
        label: 'Tier 3 · Complete Clinic OS',
        stage: 'Stage 2',
        estimate: 550,
        range: 'DKK 500-600 / seat / mo',
        summary: 'Full clinic operating system with billing, EHR, calendar, and admin tools.',
    },
];

const formatDkkAmount = (value) => `DKK ${Math.round(value).toLocaleString('da-DK')}`;

const parseDkkAmount = (value) => {
    const normalized = String(value || '')
        .replace(/\s/g, '')
        .replace(/,/g, '.')
        .replace(/[^0-9.]/g, '');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

const TRUST_BADGES = [
    { badge: 'CE', label: 'CE-Approved' },
    { badge: 'GDPR', label: 'GDPR Compliant' },
    { badge: 'EU AI', label: 'EU AI-Act Aligned' },
    { badge: 'MedCom', label: 'MedCom Integration' },
    { badge: 'EAN', label: 'EAN Billing Ready' },
    { badge: 'DHA', label: 'Danish Health Authority' },
    { badge: 'MitID', label: 'MitID Patient Login' },
];

const SHOWCASE_CHIPS = [
    'Motion capture assessment',
    'Automated journaling & reports',
    'Automated exercise delivery',
    'Home exercise builder',
    'Patient monitoring',
    'Video consultations',
    'Chat function',
    'Calendar',
    'Online payments',
    'Staff admin',
];

const SHOWCASE_ITEMS = {
    'Motion capture assessment': {
        mediaType: 'image',
        image: showcaseLaptop,
        eyebrow: 'Computer-vision intake',
        title: 'Guide patients through safe digital pre-assessments',
        text: 'Patients complete structured intake and guided tests before the visit so clinicians start with a clearer picture of symptoms, function, and red flags.',
    },
    'Automated journaling & reports': {
        mediaType: 'image',
        image: Portfolio1,
        eyebrow: 'Auto-journaling',
        title: 'Generate structured notes, status reports, and epicrisis faster',
        text: 'Clinical actions flow into the journal automatically, reducing documentation overhead while keeping the clinician in charge of the final note.',
    },
    'Automated exercise delivery': {
        mediaType: 'image',
        image: Portfolio4,
        eyebrow: 'Adaptive programs',
        title: 'Deliver phased programs with controlled progression',
        text: 'Prescribe adaptive exercise plans that respond to patient feedback while keeping guardrails defined by the treating clinician.',
    },
    'Home exercise builder': {
        mediaType: 'image',
        image: Portfolio5,
        eyebrow: 'Program builder',
        title: 'Build reusable templates and condition-specific pathways',
        text: 'Create exercise libraries, phased protocols, and education bundles that can be reused across clinicians and clinic locations.',
    },
    'Patient monitoring': {
        mediaType: 'image',
        image: Portfolio2,
        eyebrow: 'Remote monitoring',
        title: 'Track adherence, symptoms, satisfaction, and follow-up risk',
        text: 'A single clinic dashboard surfaces who is progressing, who needs attention, and which follow-ups are already partially documented.',
    },
    'Video consultations': {
        mediaType: 'video',
        poster: showcaseLaptop,
        eyebrow: 'Integrated telehealth',
        title: 'Run secure video visits inside the clinical workflow',
        text: 'The showcase is wired for a muted local autoplay demo video. Add a clinic demo asset at public/media/clinic-demo.mp4 to activate this view.',
    },
    'Chat function': {
        mediaType: 'image',
        image: Portfolio2,
        eyebrow: 'Secure messaging',
        title: 'Keep patient communication inside the care pathway',
        text: 'Secure chat supports follow-up questions, care nudges, and symptom clarification without pulling staff into fragmented channels.',
    },
    Calendar: {
        mediaType: 'image',
        image: Portfolio3,
        eyebrow: 'Scheduling',
        title: 'Coordinate clinics, staff, and appointment types from one system',
        text: 'Calendar and booking tools make it easier to manage hybrid appointment types, virtual visits, and multi-location availability.',
    },
    'Online payments': {
        mediaType: 'image',
        image: Portfolio4,
        eyebrow: 'Billing',
        title: 'Support Stripe-backed payments and digital collection flows',
        text: 'Payment setup, online collection, and insurance-related flows fit into the same patient journey instead of living in separate tools.',
    },
    'Staff admin': {
        mediaType: 'image',
        image: Portfolio5,
        eyebrow: 'Clinic operations',
        title: 'Manage roles, services, and clinic configuration centrally',
        text: 'Invite staff, structure permissions, and support growth across one or multiple clinic locations from a single operating layer.',
    },
};

const INTAKE_FEATURES = [
    {
        title: 'Structured digital intake form',
        body: 'Collect chief complaint, symptom history, PROMs, functional limitations, and red flag screening before the consultation begins.',
    },
    {
        title: 'Red flag screening built in',
        body: 'Surface risks and triage issues before the patient enters the room or joins the video consultation.',
    },
    {
        title: 'Smart auto-journaling',
        body: 'History, PROMs, diagnosis choices, exercise edits, and advice delivery contribute to a ready-to-review journal note.',
    },
    {
        title: 'Diagnosis-linked templates and workflows',
        body: 'Load condition-specific exercises, education, and follow-up patterns instantly after diagnosis selection.',
    },
    {
        title: 'Instant epicrisis and status reports',
        body: 'Generate structured documentation for referring stakeholders without repeating the same work in separate systems.',
    },
];

const EXERCISE_CARDS = [
    {
        kicker: 'Library',
        icon: FitnessCenterIcon,
        iconClassName: 'blue',
        title: 'Exercise library and program builder',
        body: 'Build phased home exercise programs with editable parameters, templates, and clinician-defined progression logic.',
    },
    {
        kicker: 'Control',
        icon: TuneIcon,
        iconClassName: 'orange',
        title: 'Clinician-controlled adaptive logic',
        body: 'Enable, adjust, or disable progression rules per exercise so the system never changes the plan outside the framework you set.',
    },
    {
        kicker: 'Journal',
        icon: AssessmentIcon,
        iconClassName: 'green',
        title: 'PROM-linked follow-up notes',
        body: 'Exercise responses, pain changes, and satisfaction data feed the journal automatically ahead of the next consultation.',
    },
    {
        kicker: 'Patient app',
        icon: PhoneIphoneIcon,
        iconClassName: 'teal',
        title: 'Guided delivery through the patient app',
        body: 'Patients receive exercises, education, reminders, and feedback requests in a single experience that keeps them engaged.',
    },
    {
        kicker: 'Insight',
        icon: NotificationsActiveIcon,
        iconClassName: 'purple',
        title: 'Smart follow-up insight delivery',
        body: 'Weekly check-ins surface trends early and prepare clinicians with concise summaries instead of raw note bloat.',
    },
    {
        kicker: 'Education',
        icon: DescriptionIcon,
        iconClassName: 'grey',
        title: 'Advice and education tracking',
        body: 'Condition-specific guidance and education content are delivered to patients and documented automatically.',
    },
];

const REMOTE_FEATURES = [
    {
        title: 'Real-time adherence and progress dashboard',
        body: 'See which patients are completing their programs, how symptoms evolve, and who needs attention this week.',
    },
    {
        title: 'Exercise feedback and symptom tracking',
        body: 'Patients rate sessions and report symptoms so clinicians can make informed adjustments between visits.',
    },
    {
        title: 'Secure in-app patient chat',
        body: 'Keep communication associated with the patient record rather than relying on disconnected channels.',
    },
    {
        title: 'Smart follow-up summaries',
        body: 'Remote check-ins prepare a structured view for the next consultation and reduce re-assessment time.',
    },
    {
        title: 'Education library delivery',
        body: 'Push condition-specific articles, videos, and recovery advice and keep distribution visible to the clinician.',
    },
];

const ADMIN_CARDS = [
    {
        kicker: 'Scheduling',
        icon: EventIcon,
        iconClassName: 'blue',
        title: 'Calendar, booking and online intake',
        body: 'Manage clinician availability, appointment types, hybrid visits, branded booking links, and pre-visit intake from one schedule.',
    },
    {
        kicker: 'Payments',
        icon: PaymentIcon,
        iconClassName: 'orange',
        title: 'Billing, subscriptions and auto-pay',
        body: 'Bring invoices, co-payments, Stripe payments, subscriptions, and automated collection into the clinic workflow.',
    },
    {
        kicker: 'Finance',
        icon: ReceiptIcon,
        iconClassName: 'green',
        title: 'Automated bookkeeping and accounting',
        body: 'Connect Appelsin to reduce reconciliation work and keep financial workflows closer to the events that create them.',
    },
    {
        kicker: 'Insurance',
        icon: BusinessIcon,
        iconClassName: 'teal',
        title: 'EAN billing and insurance workflows',
        body: 'Support EAN billing and insurance-facing administration alongside the clinical and payment records already in use.',
    },
    {
        kicker: 'Team',
        icon: PeopleIcon,
        iconClassName: 'purple',
        title: 'Staff, services and clinic locations',
        body: 'Set up staff access, clinic locations, services, and the operational structure required as your team grows.',
    },
    {
        kicker: 'Insight',
        icon: AssessmentIcon,
        iconClassName: 'grey',
        title: 'Clinic analytics and patient insight',
        body: 'Use clinic-level analytics and patient signals to understand performance, workload, and where follow-up is needed.',
    },
    {
        kicker: 'Interoperability',
        icon: ContactMailIcon,
        iconClassName: 'blue',
        title: 'MedCom epicrisis and referral reports',
        body: 'Send structured epicrises and clinical reports to GPs and referral partners without leaving the patient workflow.',
    },
    {
        kicker: 'Identity',
        icon: CheckCircleIcon,
        iconClassName: 'orange',
        title: 'MitID login and white-label access',
        body: 'Support verified patient access and a clinic-branded experience with a stronger foundation for Danish healthcare workflows.',
    },
];

const STORY_CARDS = [
    {
        image: Portfolio3,
        title: 'Digital intake before arrival',
        body: 'Move history capture and symptom mapping into the patient journey before the appointment begins.',
    },
    {
        image: Portfolio1,
        title: 'Auto-drafted first-visit documentation',
        body: 'Give clinicians a structured journal starting point instead of an empty template at the first encounter.',
    },
    {
        image: Portfolio4,
        title: 'Adaptive exercise delivery',
        body: 'Guide patients through home exercise phases while keeping treatment logic visible and reviewable.',
    },
    {
        image: Portfolio5,
        title: 'Admin workflows without switching tools',
        body: 'Tie journaling, scheduling, billing, and patient communications together in one clinic operating layer.',
    },
];

const styles = (theme) => ({
    sloganSection: {
        width: '100%',
        margin: '100px auto 32px auto',
        background: 'transparent',
        borderRadius: 16,
        padding: '32px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    complianceBanner: {
        width: '100vw',
        maxWidth: '100vw',
        position: 'relative',
        background: 'rgba(255,255,255,0.15)',
        borderRadius: 0,
        padding: '12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 48,
        boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
        marginBottom: 58,
        zIndex: 50,
    },
    complianceLogo: {
        height: 48,
        width: 'auto',
        opacity: 0.98,
        [theme.breakpoints.down('sm')]: {
            height: 40,
        },
        [theme.breakpoints.up('md')]: {
            height: 55,
        },
        [theme.breakpoints.up('lg')]: {
            height: 92,
        },
    },
    complianceLogo2: {
        height: 'auto',
        width: 'auto',
        opacity: 0.98,
        [theme.breakpoints.down('sm')]: {
            height: 27,
        },
        [theme.breakpoints.up('md')]: {
            height: 30,
        },
        [theme.breakpoints.up('lg')]: {
            height: 45,
        },
    },
    benefitImageGrid: {
        background: '#fff',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 180,
        margin: 10,
        boxShadow: '0 4px 24px 0 rgba(60,60,60,0.10)',
    },
    benefitImage: {
        width: '95%',
        maxWidth: 340,
        minWidth: 180,
        height: 'auto',
        borderRadius: 16,
        objectFit: 'cover',
        alignSelf: 'center',
        background: '#f5f7fa',
    },
    benefitTextGrid: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'left',
        padding: '24px 32px',
        minHeight: 180,
    },
    benefitTextBox: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '24px 32px',
    },
    benefitHeadline: {
        color: '#002D55',
        fontWeight: 600,
        textAlign: 'left',
        maxWidth: 600,
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.1rem',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.2rem',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '1.3rem',
        },
        [theme.breakpoints.up('lg')]: {
            fontSize: '1.5rem',
        },
        [theme.breakpoints.up('xl')]: {
            fontSize: '1.7rem',
        },
    },
    benefitHeadline2: {
        color: '#002D55',
        fontWeight: 600,
        textAlign: 'center',
        maxWidth: 600,
        /* center this block horizontally within its container */
        margin: '0 auto',
        display: 'block',
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.1rem',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.2rem',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '1.3rem',
        },
        [theme.breakpoints.up('lg')]: {
            fontSize: '1.5rem',
        },
        [theme.breakpoints.up('xl')]: {
            fontSize: '1.7rem',
        },
    },
    benefitLine: {
        width: 80,
        margin: '0px 0 8px 0',
    },
    benefitText: {
        color: '#324158',
        fontWeight: 400,
        textAlign: 'left',
        maxWidth: 600,
        [theme.breakpoints.down('xs')]: {
            fontSize: '0.9rem',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.0rem',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '1.1rem',
        },
        [theme.breakpoints.up('lg')]: {
            fontSize: '1.2rem',
        },
    },
    root: {
        flexGrow: 1,
    },
    section: {
        background: 'linear-gradient(90deg, #dce4eb, #d7d4cf)',
        borderRadius: 16,
        boxShadow: '0 4px 24px 0 rgba(60,60,60,0.12)',
        border: '1px solid #e0e0e0',
        maxWidth: 1200,
        width: '96%',
        margin: '32px auto',
        padding: 36,
    },
    section2: {
        background: 'transparent',
        //boxShadow: '0 4px 24px 0 rgba(60,60,60,0.12)',
        //border: '1px solid #e0e0e0',
        margin: '32px auto',
        width: '100vw',
        maxWidth: '100vw',
        position: 'relative',
        borderRadius: 0,
        padding: '8px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 58,
        //boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
        marginBottom: 48,
        zIndex: 50,
        marginTop: 0
    },
    headline: {
        color: '#002D55',
        fontWeight: 600,
        marginBottom: 12,
        textAlign: 'left',
        maxWidth: 700,
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.3rem',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.3rem',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '1.5rem',
        },
        [theme.breakpoints.up('lg')]: {
            fontSize: '1.8rem',
        },
        [theme.breakpoints.up('xl')]: {
            fontSize: '2rem',
        },
    },
    subheadline: {
        color: '#324158',
        fontWeight: 400,
        marginBottom: 8,
        textAlign: 'left',
        maxWidth: 600,
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.0rem',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.0rem',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '1.1rem',
        },
        [theme.breakpoints.up('lg')]: {
            fontSize: '1.4rem',
        },
    },
    sloganHeadline: {
        color: '#002D55',
        fontWeight: 600,
        marginBottom: 24,
        textAlign: 'center',
        maxWidth: 900,
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.3rem',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.3rem',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '1.4rem',
        },
        [theme.breakpoints.up('lg')]: {
            fontSize: '1.6rem',
        },
        [theme.breakpoints.up('xl')]: {
            fontSize: '1.8rem',
        },
    },
    buttonRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        marginTop: "1.5%",
        marginBottom: 24,
        alignItems: 'center',
    },
    orangeFocused: {
        backgroundColor: '#FFFFFF',
        '& .MuiOutlinedInput-root': {
            borderRadius: 14,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF7F27',
        },
        '& .MuiInputLabel-outlined': {
            color: '#002D55',
        },
        '& .MuiInputLabel-outlined.Mui-focused': {
            color: '#002D55',
        },
    },
    orangeSelect: {
        backgroundColor: '#FFFFFF',
        '& .MuiOutlinedInput-root': {
            borderRadius: 14,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF7F27',
        },
        '& .MuiInputLabel-outlined': {
            color: '#002D55',
        },
        '& .MuiInputLabel-outlined.Mui-focused': {
            color: '#002D55',
        },
        '& .MuiInputLabel-shrink': {
            color: '#002D55',
        },
        '& .MuiSelect-root': {
            color: '#002D55',
        },
    },
    signupButton: {
        color: '#FFFFFF',
        backgroundColor: '#002D55',
        borderRadius: 12,
        border: 'none',
        boxShadow: 'none',
        padding: '6px 16px',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#FF7F27',
        },
        '&.Mui-disabled': {
            backgroundColor: '#9e9e9e',
            color: '#fff',
        },
    },
    signupButtonSecondary: {
        backgroundColor: '#e6eaf0',
        color: '#002D55',
        fontWeight: 600,
        borderRadius: 12,
        border: 'none',
        boxShadow: 'none',
        padding: '6px 16px',
        '&:hover': {
            backgroundColor: '#e6eaf0',
            color: '#FF7F27',
        },
    },
    signupStepper: {
        '& .MuiStepLabel-label': {
            color: '#002D55',
        },
        '& .MuiStepLabel-label.MuiStepLabel-active': {
            color: '#002D55',
            fontWeight: 600,
        },
        '& .MuiStepLabel-label.MuiStepLabel-completed': {
            color: '#002D55',
        },
    },
    checkboxGroup: {
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        border: '1px solid rgba(0, 0, 0, 0.23)',
        padding: '6px 12px',
        '&:hover': {
            border: '1px solid rgba(0, 0, 0, 0.87)',
        },
        '& .MuiFormControlLabel-label': {
            color: '#002D55',
            fontSize: '0.9rem',
        },
    },
});



const roundedCheckboxIcon = (
    <span style={{ display: 'inline-block', boxSizing: 'border-box', width: '1em', height: '1em', border: '2px solid currentColor', borderRadius: 16 }} />
);
const roundedCheckboxCheckedIcon = (
    <span style={{ display: 'inline-flex', boxSizing: 'border-box', width: '1em', height: '1em', background: 'currentColor', borderRadius: 16, alignItems: 'center', justifyContent: 'center' }}>
        <svg viewBox="0 0 24 24" style={{ width: '0.7em', height: '0.7em', fill: '#fff' }}>
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
    </span>
);
const roundedCheckboxIndeterminateIcon = (
    <span style={{ display: 'inline-flex', boxSizing: 'border-box', width: '1em', height: '1em', background: 'currentColor', borderRadius: 16, alignItems: 'center', justifyContent: 'center' }}>
        <svg viewBox="0 0 24 24" style={{ width: '0.7em', height: '0.7em', fill: '#fff' }}>
            <path d="M19 13H5v-2h14v2z" />
        </svg>
    </span>
);

// Stripe Setup Form for signup (must be outside main component to use Stripe hooks)
const SignupSetupForm = ({ onSuccess, onBack }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setLoading(true);
        setError(null);
        const result = await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });
        if (result.error) {
            setError(result.error.message);
            setLoading(false);
        } else {
            // Pass the confirmed payment method ID back to the parent
            const pmId = result.setupIntent && result.setupIntent.payment_method;
            onSuccess(pmId || null);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            {error && (
                <Typography style={{ color: 'red', marginTop: 8, fontSize: 13 }}>{error}</Typography>
            )}
            <Box style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <Button type="submit" variant="contained" disabled={!stripe || loading}
                    style={{ flex: 1, backgroundColor: '#002D55', color: '#fff', borderRadius: 12, fontWeight: 600 }}>
                    {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Verify Card'}
                </Button>
            </Box>
        </form>
    );
};

function ClinicContent({ signupRequestKey = 0, classes }) {
    const history = useHistory();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
    const autoVerifyTimerRef = useRef(null);
    const lastAutoVerifiedKeyRef = useRef('');
    const autofilledBusinessFieldsRef = useRef(null);
    const activeVerificationRequestRef = useRef(0);
    // Sign up dialog state
    const [openSignup, setOpenSignup] = useState(false);
    const [confirmCloseSignupOpen, setConfirmCloseSignupOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    // Stepper state for signup wizard
    const [signupStep, setSignupStep] = useState(0);
    // Stripe onboarding data returned from backend
    const [stripeOnboardingUrl, setStripeOnboardingUrl] = useState(null);
    const [signupEncryptedId, setSignupEncryptedId] = useState(null);
    const [signupRoleType, setSignupRoleType] = useState(null);
    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);
    const [stripeLanguage, setStripeLanguage] = useState('en');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [paymentMethodId, setPaymentMethodId] = useState(null);
    const [fetchingSetupIntent, setFetchingSetupIntent] = useState(false);
    // CVR verification state
    const [cvrVerifying, setCvrVerifying] = useState(false);
    const [cvrVerified, setCvrVerified] = useState(null); // null = not checked, true/false = result
    const [cvrVerifiedName, setCvrVerifiedName] = useState('');
    const [cvrMessage, setCvrMessage] = useState('');
    const [insuranceCatalog, setInsuranceCatalog] = useState([]);
    const [form, setForm] = useState({
        email: '',
        //password: '',
        name_business: '',
        firstName_mainContactPerson: '',
        lastName_mainContactPerson: '',
        business_registration_number: '',
        care_provider_type: 'clinic',
        address: '',
        city: '',
        phoneNumber_CareProvider: '',
        phoneNumber_ContactPerson: '',
        email_CareProvider: '',
        email_contactPerson: '',
        postal_code: '',
        country: '',
        notes: '',
        role_type: 'careprovider_admin',
        // New fields
        public_subsidy: 'no',
        supports_insurance: 'no',
        supported_insurance_networks: [],
        services_selected: '',
        specialities: {
            neck: false,
            shoulder: false,
            upper_back: false,
            lower_back: false,
            hip: false,
            thigh: false,
            knee: false,
            ankle: false,
            foot: false,
            calf: false,
            heel: false,
            headache: false,
            concussion: false,
        },
        clinic_locations: [{ name: '', address: '', city: '', postal_code: '', country: '', phone: '', email: '', same_as_business: false }],
    });

    const [cvrChecked, setCvrChecked] = useState(false); // true once verify has run (or user skips)

    const normalizeRegistrationNumber = (value, country = '') => {
        const rawValue = String(value || '').trim();
        if (!rawValue) return '';

        const compactValue = rawValue.replace(/\s+/g, '').trim();
        if (country === 'Denmark') {
            const labeledMatch = rawValue.match(/CVR(?!P)(?:[-\s.:/]?(?:NR|NUMMER))?[^0-9]*([0-9]{8})(?![0-9])/i);
            if (labeledMatch) {
                return labeledMatch[1];
            }

            const standaloneMatch = rawValue.match(/(?:^|[^0-9])([0-9]{8})(?![0-9])/);
            if (standaloneMatch) {
                return standaloneMatch[1];
            }
        }

        return compactValue;
    };

    const clearAutofilledBusinessFields = () => {
        const autofilledValues = autofilledBusinessFieldsRef.current;
        if (!autofilledValues) {
            return;
        }

        setForm((currentForm) => {
            let changed = false;
            const nextForm = { ...currentForm };

            ['name_business', 'address', 'city', 'postal_code'].forEach((field) => {
                if (autofilledValues[field] && currentForm[field] === autofilledValues[field]) {
                    nextForm[field] = '';
                    changed = true;
                }
            });

            return changed ? nextForm : currentForm;
        });

        autofilledBusinessFieldsRef.current = null;
    };

    const isRegistrationReadyForAutoVerify = (regNum, country) => {
        const normalized = normalizeRegistrationNumber(regNum, country);
        if (!normalized || !country) return false;

        if (country === 'Denmark') return /^\d{8}$/.test(normalized);
        if (country === 'Norway') return /^\d{9}$/.test(normalized);
        if (country === 'United Kingdom') return normalized.length >= 8;

        return normalized.length >= 8;
    };

    // Auto-verify once CVR + country are both set
    const triggerAutoVerify = async (regNum, country, options = {}) => {
        const normalizedRegNum = normalizeRegistrationNumber(regNum, country);
        if (!normalizedRegNum || !country) return;

        const requestId = activeVerificationRequestRef.current + 1;
        activeVerificationRequestRef.current = requestId;
        clearAutofilledBusinessFields();
        setCvrVerifying(true);
        setCvrVerified(null);
        setCvrVerifiedName('');
        setCvrMessage('');
        setCvrChecked(false);
        try {
            const res = await fetch(`${BACKEND_URL}/account/verify-business/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    registration_number: normalizedRegNum,
                    country,
                    use_vies_fallback: Boolean(options.useViesFallback),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (activeVerificationRequestRef.current !== requestId) {
                return;
            }
            if (data.verified === true) {
                const autofilledValues = {
                    name_business: data.business_name || '',
                    address: data.address || '',
                    city: data.city || '',
                    postal_code: data.postal_code || '',
                };

                autofilledBusinessFieldsRef.current = autofilledValues;
                setCvrVerified(true);
                setCvrVerifiedName(data.business_name || '');
                setCvrMessage(`Verified: ${data.business_name || 'Business found'}`);
                setForm((f) => ({
                    ...f,
                    name_business: autofilledValues.name_business || f.name_business,
                    address: autofilledValues.address || f.address,
                    city: autofilledValues.city || f.city,
                    postal_code: autofilledValues.postal_code || f.postal_code,
                }));
            } else if (data.verified === false) {
                autofilledBusinessFieldsRef.current = null;
                setCvrVerified(false);
                setCvrMessage(data.message || 'Business not found. You can still fill in the fields manually.');
            } else {
                autofilledBusinessFieldsRef.current = null;
                setCvrVerified(null);
                setCvrMessage(data.message || 'Verification not available for this country.');
            }
        } catch {
            if (activeVerificationRequestRef.current !== requestId) {
                return;
            }
            autofilledBusinessFieldsRef.current = null;
            setCvrVerified(null);
            setCvrMessage('Could not reach verification service.');
        } finally {
            if (activeVerificationRequestRef.current === requestId) {
                setCvrVerifying(false);
                setCvrChecked(true);
            }
        }
    };

    const openSignupDialog = () => { setSignupStep(0); setStripeOnboardingUrl(null); setSignupEncryptedId(null); setSetupIntentClientSecret(null); setCvrVerified(null); setCvrMessage(''); setCvrChecked(false); setSelectedPlan(null); setPaymentMethodId(null); setOpenSignup(true); };
    const closeSignupDialog = () => {
        setConfirmCloseSignupOpen(false);
        setOpenSignup(false);
    };
    const handleSignupDialogClose = (event, reason) => {
        if (reason === 'backdropClick') {
            setConfirmCloseSignupOpen(true);
            return;
        }
        closeSignupDialog();
    };

    useEffect(() => {
        if (!signupRequestKey) {
            return;
        }

        openSignupDialog();
    }, [signupRequestKey]);

    useEffect(() => {
        let cancelled = false;

        const fetchInsuranceCatalog = async () => {
            if (!openSignup) {
                return;
            }
            try {
                const response = await fetch(`${BACKEND_URL}/api/profile/public/insurance-support/catalog/?country=${encodeURIComponent(form.country || 'Denmark')}`);
                const data = await response.json().catch(() => ({}));
                if (!response.ok || cancelled) {
                    return;
                }
                setInsuranceCatalog(Array.isArray(data.insurance_support_catalog?.network_options) ? data.insurance_support_catalog.network_options : []);
            } catch {
                if (!cancelled) {
                    setInsuranceCatalog([]);
                }
            }
        };

        fetchInsuranceCatalog();
        return () => {
            cancelled = true;
        };
    }, [BACKEND_URL, form.country, openSignup]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newVal = type === 'checkbox' ? checked : value;
        if (name === 'business_registration_number' || name === 'country') {
            activeVerificationRequestRef.current += 1;
            clearAutofilledBusinessFields();
            setCvrVerified(null);
            setCvrVerifiedName('');
            setCvrMessage('');
            setCvrChecked(false);
            lastAutoVerifiedKeyRef.current = '';
        }

        setForm((f) => ({
            ...f,
            [name]: newVal,
            ...(name === 'supports_insurance' && newVal !== 'yes' ? { supported_insurance_networks: [] } : {}),
        }));
    };

    const handleInsuranceNetworkToggle = (networkValue) => (event) => {
        const checked = event.target.checked;
        setForm((f) => {
            const currentValues = Array.isArray(f.supported_insurance_networks) ? f.supported_insurance_networks : [];
            const nextValues = checked
                ? [...currentValues, networkValue]
                : currentValues.filter((item) => item !== networkValue);
            return {
                ...f,
                supports_insurance: nextValues.length > 0 ? 'yes' : 'no',
                supported_insurance_networks: Array.from(new Set(nextValues)),
            };
        });
    };

    useEffect(() => {
        const regNum = form.business_registration_number;
        const country = form.country;
        const normalized = normalizeRegistrationNumber(regNum, country);

        if (autoVerifyTimerRef.current) {
            clearTimeout(autoVerifyTimerRef.current);
            autoVerifyTimerRef.current = null;
        }

        if (!isRegistrationReadyForAutoVerify(normalized, country)) {
            return undefined;
        }

        const requestKey = `${country}:${normalized}`;
        if (requestKey === lastAutoVerifiedKeyRef.current) {
            return undefined;
        }

        autoVerifyTimerRef.current = setTimeout(() => {
            lastAutoVerifiedKeyRef.current = requestKey;
            triggerAutoVerify(normalized, country);
        }, 700);

        return () => {
            if (autoVerifyTimerRef.current) {
                clearTimeout(autoVerifyTimerRef.current);
                autoVerifyTimerRef.current = null;
            }
        };
    }, [form.business_registration_number, form.country]);

    const handleSpecialityChange = (key) => (e) => {
        const checked = e.target.checked;
        setForm((f) => ({ ...f, specialities: { ...f.specialities, [key]: checked } }));
    };

    // Clinic location helpers
    const handleLocationChange = (idx, field, value) => {
        setForm((f) => {
            const locs = [...f.clinic_locations];
            locs[idx] = { ...locs[idx], [field]: value };
            return { ...f, clinic_locations: locs };
        });
    };
    const handleLocationSameAsBusiness = (idx, checked) => {
        setForm((f) => {
            const locs = [...f.clinic_locations];
            if (checked) {
                locs[idx] = { ...locs[idx], same_as_business: true, address: f.address, city: f.city, postal_code: f.postal_code, country: f.country };
            } else {
                locs[idx] = { ...locs[idx], same_as_business: false };
            }
            return { ...f, clinic_locations: locs };
        });
    };
    const addLocation = () => {
        setForm((f) => ({ ...f, clinic_locations: [...f.clinic_locations, { name: '', address: '', city: '', postal_code: '', country: '', phone: '', email: '', same_as_business: false }] }));
    };
    const removeLocation = (idx) => {
        setForm((f) => ({ ...f, clinic_locations: f.clinic_locations.filter((_, i) => i !== idx) }));
    };

    // Manual re-verify button
    const handleVerifyBusiness = () => triggerAutoVerify(form.business_registration_number, form.country, { useViesFallback: true });

    // Helper: label for business registration field based on country
    const getRegLabel = () => {
        const c = form.country;
        if (c === 'Denmark') return 'CVR Number';
        if (c === 'United Kingdom') return 'Company Number';
        if (c === 'Norway') return 'Org. Nr.';
        return 'Business Registration / VAT Number';
    };

    // Step validation
    const isStep1Valid = () =>
        form.name_business &&
        form.business_registration_number &&
        form.country &&
        form.address &&
        form.city &&
        form.postal_code &&
        cvrChecked; // must have at least attempted verification
    const isStep2Valid = () => !!form.services_selected && (form.supports_insurance !== 'yes' || (form.supported_insurance_networks || []).length > 0);
    const isStep3Valid = () => form.email && form.firstName_mainContactPerson && form.lastName_mainContactPerson && form.clinic_locations.length > 0 && form.clinic_locations.some(l => l.address);

    const handleCreateAccount = async (skipStripe = false) => {
        if (!form.email || !form.name_business || !form.firstName_mainContactPerson || !form.lastName_mainContactPerson) {
            alert('Please fill the required fields: email, business name and contact person names.');
            return;
        }
        setCreating(true);
        try {
            const payload = { ...form };
            const svc = form.services_selected;
            payload.services = svc === 'both' ? ['online', 'physical'] : svc ? [svc] : [];
            delete payload.services_selected;
            payload.specialities = Object.keys(form.specialities || {}).filter(k => form.specialities[k]);
            // Send cleaned clinic_locations (strip the same_as_business flag)
            payload.clinic_locations = (form.clinic_locations || []).map((loc) => ({
                name: loc.name || '',
                address: loc.address || '',
                city: loc.city || '',
                postal_code: loc.postal_code || '',
                country: loc.country || '',
                phone: loc.phone || '',
                email: loc.email || '',
            }));
            if (selectedPlan) payload.selected_plan = selectedPlan;
            if (paymentMethodId && paymentMethodId !== 'verified') payload.payment_method_id = paymentMethodId;
            if (cvrVerified === true) {
                payload.cvr_verified = true;
                payload.cvr_verified_source = form.country === 'Denmark' ? 'CVR' : form.country === 'United Kingdom' ? 'CompaniesHouse' : form.country === 'Norway' ? 'Brreg' : 'VIES';
            }

            const res = await fetch(`${BACKEND_URL}/account/create_care_provider/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await res.json().catch(() => ({}));
            if (res.ok && data.encrypted_id) {
                const roleTypeParam = encodeURIComponent(data.role_type || form.role_type || 'careprovider_admin');
                setSignupEncryptedId(data.encrypted_id);
                setSignupRoleType(roleTypeParam);

                if (data.stripe_onboarding_url) {
                    setStripeOnboardingUrl(data.stripe_onboarding_url);
                    try { localStorage.setItem('stripe_onboarding_encrypted_id', data.encrypted_id); } catch {}
                }

                if (!skipStripe && data.setup_intent_client_secret) {
                    // Show Stripe payment form in Step 3
                    setSetupIntentClientSecret(data.setup_intent_client_secret);
                } else {
                    // Skip Stripe or no secret — go straight to email verification
                    closeSignupDialog();
                    history.push(`/verify_user/${data.encrypted_id}?role_type=${roleTypeParam}`);
                }
                return;
            }

            if (data && data.errors) {
                const msgs = Object.entries(data.errors).map(([k, v]) => `${k}: ${v}`).join('\n');
                alert(`Validation errors:\n${msgs}`);
            } else if (data && data.error) {
                alert(data.error);
            } else {
                alert('Failed to create account.');
            }
        } catch (err) {
            console.error('Network or other error:', err);
            alert('Network error while creating account');
        } finally {
            setCreating(false);
        }
    };

    const handleSubmit = () => handleCreateAccount(false);
    const handleCreateAndSkip = () => handleCreateAccount(true);

    // After Stripe payment method saved or user skips, redirect to verify
    const handleFinishSignup = () => {
        closeSignupDialog();
        if (signupEncryptedId) {
            history.push(`/verify_user/${signupEncryptedId}?role_type=${signupRoleType || 'careprovider_admin'}`);
        }
    };

    const handleOpenStripeOnboarding = () => {
        if (!stripeOnboardingUrl) {
            return;
        }

        window.open(stripeOnboardingUrl, '_blank', 'noopener,noreferrer');
    };

    // Fetch a customerless SetupIntent so card can be verified before account creation
    const handleFetchSetupIntent = async () => {
        setFetchingSetupIntent(true);
        try {
            const res = await fetch(`${BACKEND_URL}/account/setup-intent/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: form.email }),
            });
            const data = await res.json().catch(() => ({}));
            if (data.client_secret) {
                setSetupIntentClientSecret(data.client_secret);
            } else {
                alert(data.error || 'Could not initialise payment setup. Please try again.');
            }
        } catch {
            alert('Network error while setting up payment. You can skip and add a card later.');
        } finally {
            setFetchingSetupIntent(false);
        }
    };

    // Called by SignupSetupForm after Stripe confirms the SetupIntent
    const handleCardVerified = (pmId) => {
        setPaymentMethodId(pmId || 'verified');
    };
    // FAQ expand/collapse state
    const [openFaq, setOpenFaq] = useState(null);
    // Showcase state for landing-page media switching
    const [selectedOverlay, setSelectedOverlay] = useState('Motion capture assessment');
    const [videoLoadFailed, setVideoLoadFailed] = useState(false);
    const [heroSignupLabel, setHeroSignupLabel] = useState('Sign-up for our BETA test');
    const [savingsCalculatorOpen, setSavingsCalculatorOpen] = useState(false);
    const [savingsCalculatorStep, setSavingsCalculatorStep] = useState(0);
    const [clinicSeats, setClinicSeats] = useState(1);
    const [customExpenses, setCustomExpenses] = useState([]);
    const [additionalTools, setAdditionalTools] = useState([]);
    const [toolSelections, setToolSelections] = useState(() => getDefaultToolSelections());
    const selectedShowcase = SHOWCASE_ITEMS[selectedOverlay] || SHOWCASE_ITEMS['Motion capture assessment'];
    const toolsMonthlyTotal = REPLACED_TOOLS.reduce((sum, tool) => {
        const selection = toolSelections[tool.id];
        return selection && selection.enabled ? sum + parseDkkAmount(selection.cost) : sum;
    }, 0) + additionalTools.reduce((sum, tool) => (
        tool.enabled ? sum + parseDkkAmount(tool.cost) : sum
    ), 0);
    const customExpensesTotal = customExpenses.reduce((sum, expense) => sum + parseDkkAmount(expense.cost), 0);
    const monthlySubscriptionSpend = toolsMonthlyTotal + customExpensesTotal;

    const handleToolToggle = (id) => {
        setToolSelections((prev) => ({ ...prev, [id]: { ...prev[id], enabled: !prev[id].enabled } }));
    };

    const handleToolCostChange = (id, value) => {
        setToolSelections((prev) => ({ ...prev, [id]: { ...prev[id], cost: value } }));
    };

    const handleToolLabelChange = (id, value) => {
        setToolSelections((prev) => ({ ...prev, [id]: { ...prev[id], label: value } }));
    };

    const addAdditionalTool = () => {
        setAdditionalTools((tools) => [...tools, {
            id: `additional-${Date.now()}`,
            enabled: true,
            label: 'Other (add custom)',
            cost: '0',
        }]);
    };

    const updateAdditionalTool = (id, field, value) => {
        setAdditionalTools((tools) => tools.map((tool) => (
            tool.id === id ? { ...tool, [field]: value } : tool
        )));
    };

    useEffect(() => {
        if (savingsCalculatorOpen) {
            setToolSelections((currentSelections) => {
                if (Object.keys(currentSelections).length !== REPLACED_TOOLS.length) {
                    return getDefaultToolSelections();
                }
                return currentSelections;
            });
        }
    }, [savingsCalculatorOpen]);

    const updateCustomExpense = (id, field, value) => {
        setCustomExpenses((expenses) => expenses.map((expense) => (
            expense.id === id ? { ...expense, [field]: value } : expense
        )));
    };

    const removeCustomExpense = (id) => {
        setCustomExpenses((expenses) => expenses.filter((expense) => expense.id !== id));
    };

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(max-width: 599px)');
        const updateLabel = () => setHeroSignupLabel(mediaQuery.matches ? 'Start free' : 'Sign-up to our BETA test for free');

        updateLabel();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', updateLabel);
            return () => mediaQuery.removeEventListener('change', updateLabel);
        }

        mediaQuery.addListener(updateLabel);
        return () => mediaQuery.removeListener(updateLabel);
    }, []);

    const handleShowcaseSelect = (label) => {
        setSelectedOverlay(label);
        if (label === 'Video consultations') {
            setVideoLoadFailed(false);
        }
    };

    // Custom step icon renderer: circular orange background with white number/check
    const CustomStepIcon = (props) => {
        const { active, completed, icon } = props;
        const size = 34;
        const baseStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: size,
            height: size,
            borderRadius: '50%',
            background: (active || completed) ? '#FF7F27' : '#bdbdbd',
            color: '#fff',
            fontWeight: 700,
            boxShadow: active ? '0 6px 18px rgba(0,0,0,0.16)' : 'none',
            fontSize: 14,
        };

        return (
            <div style={baseStyle} aria-hidden>
                {completed ? <CheckIcon style={{ fontSize: 18, color: '#fff' }} /> : <span>{icon}</span>}
            </div>
        );
    };

    return (
        <Grid container justifyContent="center" direction="column" alignItems="center" className={classes.root} style={{width: '100%', margin: 'auto', paddingLeft: 0, paddingRight: 0}}>
            {/* Sign up / onboarding dialog for creating a CareProvider — 3-step wizard */}
            <Dialog open={openSignup} onClose={handleSignupDialogClose} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 18, backgroundColor: '#f7faff' } }}>
                <DialogTitle style={{ paddingBottom: 0, color: '#002D55' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {signupStep === 0 && <BusinessIcon style={{ color: '#FF7F27' }} />}
                        {signupStep === 1 && <PeopleIcon style={{ color: '#FF7F27' }} />}
                        {signupStep === 2 && <ContactMailIcon style={{ color: '#FF7F27' }} />}
                        {signupStep === 3 && <PaymentIcon style={{ color: '#FF7F27' }} />}
                        <span style={{ color: '#002D55', fontWeight: 'bold' }}>Clinic Onboarding</span>
                    </div>
                    <IconButton style={{ position: 'absolute', right: 12, top: 12 }} onClick={closeSignupDialog}>
                        <CloseIcon />
                    </IconButton>
                    {/* Stepper header */}
                    <Stepper activeStep={signupStep} alternativeLabel style={{ background: 'transparent', padding: '16px 0 0 0', paddingTop: 30 }} className={classes.signupStepper}>
                        <Step completed={signupStep > 0}><StepLabel StepIconComponent={CustomStepIcon}>Business Details</StepLabel></Step>
                        <Step completed={signupStep > 1}><StepLabel StepIconComponent={CustomStepIcon}>Type of patients</StepLabel></Step>
                        <Step completed={signupStep > 2}><StepLabel StepIconComponent={CustomStepIcon}>Location &amp; Contact Details</StepLabel></Step>
                        <Step completed={signupStep > 3}><StepLabel StepIconComponent={CustomStepIcon}>Set Up Payments</StepLabel></Step>
                    </Stepper>
                </DialogTitle>
                <DialogContent style={{ minHeight: 300, paddingTop: 16 }}>
                    {/* ─── STEP 1: Business Details ─── */}
                    {signupStep === 0 && (
                        <Grid container spacing={2}>
                            {/* ── Phase 1: Country + CVR (always visible) ── */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required variant="outlined" className={classes.orangeSelect}>
                                    <InputLabel id="country-label">Country</InputLabel>
                                    <Select labelId="country-label" label="Country" value={form.country} name="country" onChange={handleChange}
                                        MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null }}>
                                        {['Denmark','United Kingdom','Norway','Sweden','Finland','Germany','France','Netherlands','Belgium','Spain','Italy','Poland','Austria','Ireland','Portugal','Greece','Czech Republic','Hungary','Romania','Bulgaria','Slovakia','Slovenia','Estonia','Latvia','Lithuania','Luxembourg','Croatia','Cyprus','Malta'].map((c) => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                    <TextField label={getRegLabel()} name="business_registration_number" value={form.business_registration_number} onChange={handleChange} fullWidth required variant="outlined"
                                        className={classes.orangeFocused}
                                        helperText={cvrVerifying ? 'Verifying…' : cvrMessage}
                                        InputProps={{
                                            endAdornment: cvrVerifying
                                                ? <CircularProgress size={18} />
                                                : cvrVerified === true
                                                    ? <CheckCircleIcon style={{ color: '#4caf50' }} />
                                                    : cvrVerified === false
                                                        ? <WarningIcon style={{ color: '#ff9800' }} />
                                                        : null,
                                        }}
                                    />
                                    {cvrChecked && (
                                        <Button size="small" variant="contained" onClick={handleVerifyBusiness} disabled={cvrVerifying || !form.business_registration_number || !form.country}
                                            className={classes.signupButton} style={{ minWidth: 72, marginTop: 12, height: 40 }}>
                                            Re-verify
                                        </Button>
                                    )}
                                </div>
                            </Grid>

                            {/* ── Phase 2: rest of fields — only shown once CVR check has run ── */}
                            {cvrChecked && (<>
                            <Grid item xs={12} md={6}>
                                <TextField label="Business name" name="name_business" value={form.name_business} onChange={handleChange} fullWidth required variant="outlined"
                                    className={classes.orangeFocused}
                                    helperText={cvrVerified === true && cvrVerifiedName ? `Auto-filled from registry: ${cvrVerifiedName}` : ''} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth variant="outlined" className={classes.orangeSelect}>
                                    <InputLabel id="care-type-label">Care provider type</InputLabel>
                                    <Select labelId="care-type-label" label="Care provider type" value={form.care_provider_type} name="care_provider_type" onChange={handleChange}
                                        MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null }}>
                                        <MenuItem value="clinic">Clinic</MenuItem>
                                        <MenuItem value="physiotherapist">Physiotherapist</MenuItem>
                                        <MenuItem value="public_rehabilitation_center">Public Rehabilitation Center</MenuItem>
                                        <MenuItem value="private_care_provider">Private Care Provider</MenuItem>
                                        <MenuItem value="eldercare_provider">Eldercare Provider</MenuItem>
                                        <MenuItem value="fitness_center">Fitness Center</MenuItem>
                                        <MenuItem value="insurance_company">Insurance Company</MenuItem>
                                        <MenuItem value="hospital">Hospital</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Business address" name="address" value={form.address} onChange={handleChange} fullWidth variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="City" name="city" value={form.city} onChange={handleChange} fullWidth variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Postal code" name="postal_code" value={form.postal_code} onChange={handleChange} fullWidth variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label="Notes (optional)" name="notes" value={form.notes} onChange={handleChange} fullWidth multiline rows={2} variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            </>)}
                        </Grid>
                    )}
                    {/* ─── STEP 2: Patient Profile ─── */}
                    {signupStep === 1 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" style={{ color: '#555', marginBottom: 8 }}>
                                    Tell us about the patients your clinic intends to treat. This helps power the lead generator and match you with the right patients.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth variant="outlined" className={classes.orangeSelect}>
                                    <InputLabel id="public-subsidy-label">Public subsidy</InputLabel>
                                    <Select labelId="public-subsidy-label" label="Public subsidy" value={form.public_subsidy} name="public_subsidy" onChange={handleChange}
                                        MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null }}>
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth variant="outlined" className={classes.orangeSelect}>
                                    <InputLabel id="insurance-subsidy-label">Insurance subsidy</InputLabel>
                                    <Select labelId="insurance-subsidy-label" label="Insurance subsidy" value={form.supports_insurance} name="supports_insurance" onChange={handleChange}
                                        MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null }}>
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {form.supports_insurance === 'yes' && (
                                <Grid item xs={12}>
                                    <div style={{ backgroundColor: '#FFFFFF', borderRadius: 24, border: '1px solid rgba(0,0,0,0.23)', padding: '12px 16px' }}>
                                        <Typography variant="body2" style={{ fontWeight: 600, color: '#002D55', marginBottom: 4 }}>
                                            Select the insurance networks your clinic cooperates with
                                        </Typography>
                                        <Typography variant="caption" style={{ color: '#5a6b7d', display: 'block', marginBottom: 10 }}>
                                            This list is used in public clinic matching and later staff-level exclusions.
                                        </Typography>
                                        <FormGroup row>
                                            {insuranceCatalog.map((option) => (
                                                <FormControlLabel
                                                    key={option.value}
                                                    control={(
                                                        <Checkbox
                                                            checked={(form.supported_insurance_networks || []).includes(option.value)}
                                                            onChange={handleInsuranceNetworkToggle(option.value)}
                                                            style={{ color: (form.supported_insurance_networks || []).includes(option.value) ? '#0fbb51' : '#002D55' }}
                                                            icon={roundedCheckboxIcon}
                                                            checkedIcon={roundedCheckboxCheckedIcon}
                                                        />
                                                    )}
                                                    label={option.label}
                                                />
                                            ))}
                                        </FormGroup>
                                        {(form.supported_insurance_networks || []).length === 0 && (
                                            <Typography variant="caption" style={{ color: '#c15b1b' }}>
                                                Choose at least one insurer to enable insurance-based matching.
                                            </Typography>
                                        )}
                                    </div>
                                </Grid>
                            )}
                            
                            <Grid item xs={12}>
                                <div style={{ backgroundColor: '#FFFFFF', borderRadius: 24, border: '1px solid rgba(0,0,0,0.23)', padding: '10px 16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontWeight: 600, color: '#002D55', fontSize: '0.95rem' }}>Select which specialities you want to match with patients on</span>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    style={{ color: Object.values(form.specialities).every(Boolean) ? '#0fbb51' : '#002D55' }}
                                                    icon={roundedCheckboxIcon}
                                                    checkedIcon={roundedCheckboxCheckedIcon}
                                                    indeterminateIcon={roundedCheckboxIndeterminateIcon}
                                                    checked={Object.values(form.specialities).every(Boolean)}
                                                    indeterminate={Object.values(form.specialities).some(Boolean) && !Object.values(form.specialities).every(Boolean)}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setForm((f) => ({
                                                            ...f,
                                                            specialities: Object.fromEntries(Object.keys(f.specialities).map(k => [k, checked]))
                                                        }));
                                                    }}
                                                />
                                            }
                                            label={<span style={{ color: '#002D55', fontSize: '0.85rem', fontWeight: 600 }}>Select all</span>}
                                            style={{ marginRight: 0 }}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '2px 0' }}>
                                        <FormControlLabel control={<Checkbox checked={form.specialities.neck} onChange={handleSpecialityChange('neck')} style={{ color: form.specialities.neck ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Neck" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.shoulder} onChange={handleSpecialityChange('shoulder')} style={{ color: form.specialities.shoulder ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Shoulder" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.upper_back} onChange={handleSpecialityChange('upper_back')} style={{ color: form.specialities.upper_back ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Upper back" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.lower_back} onChange={handleSpecialityChange('lower_back')} style={{ color: form.specialities.lower_back ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Lower back" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.hip} onChange={handleSpecialityChange('hip')} style={{ color: form.specialities.hip ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Hip" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.thigh} onChange={handleSpecialityChange('thigh')} style={{ color: form.specialities.thigh ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Thigh" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.knee} onChange={handleSpecialityChange('knee')} style={{ color: form.specialities.knee ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Knee" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.ankle} onChange={handleSpecialityChange('ankle')} style={{ color: form.specialities.ankle ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Ankle" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.foot} onChange={handleSpecialityChange('foot')} style={{ color: form.specialities.foot ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Foot" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.calf} onChange={handleSpecialityChange('calf')} style={{ color: form.specialities.calf ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Calf" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.heel} onChange={handleSpecialityChange('heel')} style={{ color: form.specialities.heel ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Heel" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.headache} onChange={handleSpecialityChange('headache')} style={{ color: form.specialities.headache ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Headache" />
                                        <FormControlLabel control={<Checkbox checked={form.specialities.concussion} onChange={handleSpecialityChange('concussion')} style={{ color: form.specialities.concussion ? '#0fbb51' : '#002D55' }} icon={roundedCheckboxIcon} checkedIcon={roundedCheckboxCheckedIcon} />} label="Concussion" />
                                    </div>
                                </div>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required variant="outlined" className={classes.orangeSelect}>
                                    <InputLabel id="services-label">Services offered</InputLabel>
                                    <Select
                                        labelId="services-label"
                                        label="Services offered"
                                        value={form.services_selected}
                                        onChange={(e) => setForm((f) => ({ ...f, services_selected: e.target.value }))}
                                        MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null }}
                                    >
                                        <MenuItem value="online">Online</MenuItem>
                                        <MenuItem value="physical">Physical consultations</MenuItem>
                                        <MenuItem value="both">Both online and physical consultations</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}
                    {/* ─── STEP 3: Location & Contact Details ─── */}
                    {signupStep === 2 && (
                        <Grid container spacing={2}>
                            {/* Contact person fields */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>Contact Person</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Contact first name" name="firstName_mainContactPerson" value={form.firstName_mainContactPerson} onChange={handleChange} fullWidth required variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Contact last name" name="lastName_mainContactPerson" value={form.lastName_mainContactPerson} onChange={handleChange} fullWidth required variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Clinic email (will be login)" name="email" value={form.email} onChange={handleChange} fullWidth required variant="outlined" className={classes.orangeFocused}
                                    helperText="This email will be used as the login for the clinic account" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Contact email" name="email_contactPerson" value={form.email_contactPerson} onChange={handleChange} fullWidth variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Clinic phone" name="phoneNumber_CareProvider" value={form.phoneNumber_CareProvider} onChange={handleChange} fullWidth variant="outlined" className={classes.orangeFocused} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField label="Contact phone" name="phoneNumber_ContactPerson" value={form.phoneNumber_ContactPerson} onChange={handleChange} fullWidth variant="outlined" className={classes.orangeFocused} />
                            </Grid>

                            {/* Clinic locations */}
                            <Grid item xs={12}>
                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Clinic Locations</Typography>
                                    <Button size="small" onClick={addLocation} style={{ color: '#FF7F27', fontWeight: 600, textTransform: 'none' }}>+ Add Location</Button>
                                </Box>
                                <Typography variant="body2" style={{ color: '#666', marginBottom: 8 }}>
                                    Add each physical clinic location where you see patients. At least one is required.
                                </Typography>
                            </Grid>
                            {form.clinic_locations.map((loc, idx) => (
                                <Grid item xs={12} key={idx}>
                                    <Paper variant="outlined" style={{ padding: '12px 16px', borderRadius: 12, borderColor: '#ddd', position: 'relative' }}>
                                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <Typography variant="body2" style={{ fontWeight: 600, color: '#002D55' }}>
                                                Location {idx + 1}
                                            </Typography>
                                            {form.clinic_locations.length > 1 && (
                                                <IconButton size="small" onClick={() => removeLocation(idx)} style={{ color: '#e53935' }}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField label="Location name" value={loc.name} onChange={(e) => handleLocationChange(idx, 'name', e.target.value)} fullWidth variant="outlined" className={classes.orangeFocused}
                                                    placeholder="e.g. Main Clinic, Downtown Branch" />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={<Checkbox checked={loc.same_as_business} onChange={(e) => handleLocationSameAsBusiness(idx, e.target.checked)} style={{ color: loc.same_as_business ? '#FF7F27' : '#002D55' }} />}
                                                    label={<span style={{ fontSize: '0.85rem', color: '#555' }}>Same as business address</span>}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField label="Address" value={loc.address} onChange={(e) => handleLocationChange(idx, 'address', e.target.value)} fullWidth variant="outlined" className={classes.orangeFocused} disabled={loc.same_as_business} />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField label="City" value={loc.city} onChange={(e) => handleLocationChange(idx, 'city', e.target.value)} fullWidth variant="outlined" className={classes.orangeFocused} disabled={loc.same_as_business} />
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <TextField label="Postal code" value={loc.postal_code} onChange={(e) => handleLocationChange(idx, 'postal_code', e.target.value)} fullWidth variant="outlined" className={classes.orangeFocused} disabled={loc.same_as_business} />
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <TextField label="Country" value={loc.country} onChange={(e) => handleLocationChange(idx, 'country', e.target.value)} fullWidth variant="outlined" className={classes.orangeFocused} disabled={loc.same_as_business} />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField label="Location phone" value={loc.phone} onChange={(e) => handleLocationChange(idx, 'phone', e.target.value)} fullWidth variant="outlined" className={classes.orangeFocused} />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField label="Location email" value={loc.email} onChange={(e) => handleLocationChange(idx, 'email', e.target.value)} fullWidth variant="outlined" className={classes.orangeFocused} />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    {/* ─── STEP 4: Choose Plan → Verify Card → Create Account ─── */}
                    {signupStep === 3 && (
                        <div style={{ padding: '12px 0' }}>
                            {/* Sub-state A: Plan selection */}
                            {!setupIntentClientSecret && !paymentMethodId && !signupEncryptedId && (
                                <>
                                    <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                                        Choose your plan
                                    </Typography>
                                    <Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
                                        Select the tier that fits your clinic. You can upgrade at any time from your dashboard.
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {SIGNUP_PLANS.map((plan) => (
                                            <Grid item xs={12} sm={4} key={plan.id}>
                                                <Paper
                                                    onClick={() => setSelectedPlan(plan.id)}
                                                    elevation={selectedPlan === plan.id ? 4 : 1}
                                                    style={{
                                                        cursor: 'pointer',
                                                        padding: '14px 14px 12px',
                                                        borderRadius: 14,
                                                        border: '2px solid ' + (selectedPlan === plan.id ? plan.accentColor : '#e0e0e0'),
                                                        backgroundColor: selectedPlan === plan.id
                                                            ? (plan.id === 'stage1' ? '#FFF8F3' : plan.id === 'stage0' ? '#F3FBF4' : '#F0F4FA')
                                                            : '#fff',
                                                        transition: 'border 0.15s, box-shadow 0.15s',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        position: 'relative',
                                                        userSelect: 'none',
                                                    }}
                                                >
                                                    {plan.badge && (
                                                        <Box style={{
                                                            position: 'absolute', top: -11, right: 10,
                                                            backgroundColor: plan.accentColor, color: '#fff',
                                                            fontSize: 9, fontWeight: 700, borderRadius: 4,
                                                            padding: '2px 7px', letterSpacing: 0.8, whiteSpace: 'nowrap',
                                                        }}>
                                                            {plan.badge}
                                                        </Box>
                                                    )}
                                                    <Typography style={{ color: '#999', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>
                                                        {plan.stage}
                                                    </Typography>
                                                    <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25, marginBottom: 8 }}>
                                                        {plan.tagline}
                                                    </Typography>
                                                    <Box style={{ marginBottom: 10, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                                        <Typography style={{ color: plan.accentColor, fontWeight: 800, fontSize: '1.25rem' }}>{plan.price}</Typography>
                                                        <Typography style={{ color: '#999', fontSize: '0.72rem' }}>{plan.priceUnit}</Typography>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        {plan.features.map((feat, fi) => (
                                                            <Box key={fi} style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginBottom: 4 }}>
                                                                <CheckIcon style={{ fontSize: 13, color: plan.accentColor, marginTop: 3, flexShrink: 0 }} />
                                                                <Typography style={{ color: '#555', fontSize: '0.75rem', lineHeight: 1.45 }}>{feat}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                    {selectedPlan === plan.id && (
                                                        <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 10, borderTop: '1px solid #eee', paddingTop: 8 }}>
                                                            <CheckCircleIcon style={{ color: plan.accentColor, fontSize: 15 }} />
                                                            <Typography style={{ color: plan.accentColor, fontSize: '0.75rem', fontWeight: 700 }}>Selected</Typography>
                                                        </Box>
                                                    )}
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            )}

                            {/* Sub-state B: Stripe card entry (SetupIntent fetched, card not yet confirmed) */}
                            {setupIntentClientSecret && !paymentMethodId && !signupEncryptedId && (
                                <>
                                    {/* Plan reminder pill */}
                                    {selectedPlan && (() => {
                                        const plan = SIGNUP_PLANS.find(p => p.id === selectedPlan);
                                        return plan ? (
                                            <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: plan.accentColor + '18', border: '1px solid ' + plan.accentColor + '55', borderRadius: 20, padding: '3px 12px', marginBottom: 16 }}>
                                                <CheckCircleIcon style={{ fontSize: 14, color: plan.accentColor }} />
                                                <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: plan.accentColor }}>{plan.stage} · {plan.price}{plan.priceUnit}</Typography>
                                            </Box>
                                        ) : null;
                                    })()}
                                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                        <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <svg width="50" height="21" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fill="#635BFF"/>
                                            </svg>
                                            <Box style={{ width: 1, height: 20, backgroundColor: '#d0d0d0' }} />
                                            <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: '0.9rem' }}>Add Payment Card</Typography>
                                        </Box>
                                        <Button size="small" onClick={() => setStripeLanguage(stripeLanguage === 'en' ? 'da' : 'en')} style={{ minWidth: 56, padding: '3px 8px', fontSize: '0.75rem' }}>
                                            <LanguageIcon style={{ fontSize: 16, marginRight: 4 }} />
                                            {stripeLanguage === 'en' ? '🇩🇰 DA' : '🇬🇧 EN'}
                                        </Button>
                                    </Box>
                                    <Box style={{ backgroundColor: '#f5f9ff', padding: '10px 14px', borderRadius: 6, marginBottom: 14, border: '1px solid #e3f2fd' }}>
                                        <Typography variant="caption" style={{ color: '#555', lineHeight: 1.6 }}>
                                            🔒 {stripeLanguage === 'da' ? 'Dit kort gemmes sikkert via Stripe. Ingen opkrævning sker nu.' : 'Your card is saved securely via Stripe. No charge is made now.'}
                                        </Typography>
                                    </Box>
                                    <Elements key={stripeLanguage} stripe={getStripePromise()} options={{ clientSecret: setupIntentClientSecret, locale: stripeLanguage }}>
                                        <SignupSetupForm onSuccess={handleCardVerified} />
                                    </Elements>
                                </>
                            )}

                            {/* Sub-state C: Card verified — ready to create account */}
                            {paymentMethodId && !signupEncryptedId && (
                                <Box style={{ textAlign: 'center', padding: '32px 16px' }}>
                                    <CheckCircleIcon style={{ fontSize: 56, color: '#4CAF50', marginBottom: 12 }} />
                                    <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                                        Card verified successfully
                                    </Typography>
                                    {selectedPlan && (() => {
                                        const plan = SIGNUP_PLANS.find(p => p.id === selectedPlan);
                                        return plan ? (
                                            <Typography variant="body2" style={{ color: '#555', marginBottom: 4 }}>
                                                Plan: <strong style={{ color: plan.accentColor }}>{plan.stage} · {plan.price} {plan.priceUnit}</strong>
                                            </Typography>
                                        ) : null;
                                    })()}
                                    <Typography variant="body2" style={{ color: '#888', marginTop: 8 }}>
                                        Click <strong>Create Account</strong> to register your clinic. Your card will be securely attached.
                                    </Typography>
                                </Box>
                            )}

                            {/* Sub-state D: Account created — Stripe done or spinner */}
                            {signupEncryptedId && (
                                <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                                    <CheckCircleIcon style={{ fontSize: 56, color: '#4CAF50' }} />
                                </Box>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '8px 24px 16px', justifyContent: 'space-between' }}>
                    {signupStep === 0 && (
                        <>
                            <Button onClick={closeSignupDialog} className={classes.signupButtonSecondary}>Cancel</Button>
                            <Button variant="contained" onClick={() => setSignupStep(1)} disabled={!isStep1Valid()}
                                className={classes.signupButton}>
                                Next: Patient Profile
                            </Button>
                        </>
                    )}
                    {signupStep === 1 && (
                        <>
                            <Button onClick={() => setSignupStep(0)} className={classes.signupButtonSecondary}>Back</Button>
                            <Button variant="contained" onClick={() => setSignupStep(2)} disabled={!isStep2Valid()}
                                className={classes.signupButton}>
                                Next: Location &amp; Contact
                            </Button>
                        </>
                    )}
                    {signupStep === 2 && (
                        <>
                            <Button onClick={() => setSignupStep(1)} className={classes.signupButtonSecondary}>Back</Button>
                            <Button variant="contained" onClick={() => setSignupStep(3)} disabled={!isStep3Valid()}
                                className={classes.signupButton}>
                                Next: Payment Setup
                            </Button>
                        </>
                    )}
                    {/* Step 4 — Sub-state A: Plan selection */}
                    {signupStep === 3 && !setupIntentClientSecret && !paymentMethodId && !signupEncryptedId && (
                        <>
                            <Button onClick={() => setSignupStep(2)} className={classes.signupButtonSecondary}>Back</Button>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Button onClick={handleCreateAndSkip} disabled={!selectedPlan || creating || fetchingSetupIntent} className={classes.signupButtonSecondary}>
                                    Skip Card
                                </Button>
                                <Button variant="contained"
                                    onClick={handleFetchSetupIntent}
                                    disabled={!selectedPlan || fetchingSetupIntent}
                                    className={classes.signupButton}>
                                    {fetchingSetupIntent
                                        ? <><CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} /> Loading...</>
                                        : 'Next: Add Card →'}
                                </Button>
                            </Box>
                        </>
                    )}
                    {/* Step 4 — Sub-state B: Card entry */}
                    {signupStep === 3 && setupIntentClientSecret && !paymentMethodId && !signupEncryptedId && (
                        <>
                            <Button onClick={() => { setSetupIntentClientSecret(null); }} className={classes.signupButtonSecondary}>Back</Button>
                            {/* The Verify Card button lives inside SignupSetupForm (form submit) */}
                        </>
                    )}
                    {/* Step 4 — Sub-state C: Card verified, ready to create account */}
                    {signupStep === 3 && paymentMethodId && !signupEncryptedId && (
                        <>
                            <Button onClick={() => { setPaymentMethodId(null); setSetupIntentClientSecret(null); }} className={classes.signupButtonSecondary} disabled={creating}>Back</Button>
                            <Button variant="contained" onClick={handleSubmit} disabled={creating} className={classes.signupButton}>
                                {creating ? <><CircularProgress size={18} style={{ color: '#fff', marginRight: 8 }} /> Creating...</> : 'Create Account →'}
                            </Button>
                        </>
                    )}
                    {/* Step 4 — Sub-state D: Account created */}
                    {signupStep === 3 && signupEncryptedId && (
                        <>
                                    {stripeOnboardingUrl ? (
                                        <Button
                                            variant="contained"
                                            onClick={handleOpenStripeOnboarding}
                                            className={classes.signupButton}
                                        >
                                            Complete Stripe Connect Setup
                                        </Button>
                                    ) : null}
                                    <Button onClick={handleFinishSignup} className={classes.signupButtonSecondary}>
                                        Continue to Email Verification
                                    </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog
                open={confirmCloseSignupOpen}
                onClose={() => setConfirmCloseSignupOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ style: { borderRadius: 18, backgroundColor: '#fff7f0' } }}
            >
                <DialogTitle style={{ color: '#002D55', paddingBottom: 8, position: 'relative' }}>
                    <IconButton
                        aria-label="Close warning dialog"
                        onClick={() => setConfirmCloseSignupOpen(false)}
                        style={{ position: 'absolute', right: 8, top: 8, color: '#324158' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 36 }}>
                        <WarningIcon style={{ color: '#FF7F27' }} />
                        <span style={{ color: '#002D55', fontWeight: 'bold' }}>Close signup?</span>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ color: '#324158' }}>
                        You clicked outside the signup form. If you close now, any unsaved onboarding progress will be lost.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: '0 24px 20px', justifyContent: 'space-between' }}>
                    <Button onClick={() => setConfirmCloseSignupOpen(false)} className={classes.signupButtonSecondary}>
                        Keep signing up
                    </Button>
                    <Button onClick={closeSignupDialog} className={classes.signupButton} variant="contained">
                        Close signup
                    </Button>
                </DialogActions>
            </Dialog>
            <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap');

                        .clinicLanding {
                            --navy: #002D55;
                            --navyLight: #0B4478;
                            --orange: #fb741a;
                            --slate: #324158;
                            --muted: #6b7a8d;
                            --cloud: #edf2f7;
                            --offWhite: #f8f9fb;
                            --border: rgba(0, 45, 85, 0.12);
                            --shadowSm: 0 8px 24px rgba(0, 45, 85, 0.08);
                            --shadowMd: 0 16px 48px rgba(0, 45, 85, 0.12);
                            width: 100%;
                            max-width: none;
                            margin: 0;
                            padding: 0 0 88px;
                            color: var(--slate);
                            font-family: 'DM Sans', sans-serif;
                            position: relative;
                            overflow: hidden;
                        }

                        .clinicLanding * {
                            box-sizing: border-box;
                        }

                        .clinicLanding,
                        .clinicLanding button,
                        .clinicLanding input,
                        .clinicLanding textarea,
                        .clinicLanding select,
                        .clinicLanding .MuiButton-root,
                        .clinicLanding .MuiTypography-root,
                        .clinicLanding .MuiInputBase-root,
                        .clinicLanding .MuiFormLabel-root {
                            font-family: 'DM Sans', sans-serif !important;
                            font-optical-sizing: auto;
                        }

                        .clinicLanding section {
                            width: 100%;
                            position: relative;
                            z-index: 1;
                        }

                        .clinicHero {
                            background: linear-gradient(135deg, #001e3c 0%, #003870 55%, #0057a8 100%);
                            color: #fff;
                            padding: 88px 6% 84px;
                            overflow: hidden;
                            width: 100%;
                            margin: 0;
                            position: relative;
                        }

                        .clinicHero::before {
                            content: '';
                            position: absolute;
                            inset: 0;
                            background: radial-gradient(ellipse 80% 60% at 50% 100%, rgba(251, 116, 26, 0.18) 0%, transparent 70%);
                            pointer-events: none;
                        }

                        .clinicHeroContent,
                        .clinicSectionContent,
                        .clinicPlansContent,
                        .clinicFinalContent,
                        .clinicTrustStripInner {
                            width: 100%;
                            max-width: 1180px;
                            margin: 0 auto;
                            padding: 0 40px;
                            position: relative;
                            z-index: 1;
                        }

                        .clinicHeroContent,
                        .clinicFeatureLayout,
                        .clinicRemoteLayout,
                        .clinicVideoHighlight {
                            display: grid;
                            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
                            gap: 32px;
                            align-items: start;
                        }

                        .clinicHeroContent {
                            grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
                            align-items: center;
                        }

                        .clinicHeroCopy {
                            background: transparent;
                            color: #fff;
                            border-radius: 0;
                            padding: 0;
                            box-shadow: none;
                            position: relative;
                            overflow: hidden;
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                            text-align: left;
                        }

                        .clinicHeroMedia {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            position: relative;
                        }

                        .clinicHeroMediaFrame {
                            width: 100%;
                            padding: 14px 14px 24px;
                            border-radius: 24px 24px 16px 16px;
                            overflow: visible;
                            background: linear-gradient(145deg, #25384c, #0b1724);
                            border: 1px solid rgba(255, 255, 255, 0.28);
                            box-shadow: 0 28px 64px rgba(0, 20, 45, 0.5);
                            position: relative;
                        }

                        .clinicHeroMediaFrame::after {
                            content: '';
                            position: absolute;
                            left: 7%;
                            right: 7%;
                            bottom: -12px;
                            height: 12px;
                            border-radius: 0 0 20px 20px;
                            background: #b7c3cf;
                            box-shadow: 0 10px 18px rgba(0, 20, 45, 0.3);
                        }

                        .clinicHeroMediaFrame img {
                            display: block;
                            width: 100%;
                            height: auto;
                            border-radius: 12px;
                            object-fit: cover;
                        }

                        .clinicHeroMediaLabel {
                            position: absolute;
                            right: -16px;
                            bottom: 34px;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            padding: 10px 12px;
                            border-radius: 8px;
                            background: #fff;
                            color: var(--navy);
                            box-shadow: 0 12px 28px rgba(0, 20, 45, 0.28);
                            font-size: 0.8rem;
                            font-weight: 700;
                        }

                        .clinicHeroMediaLabel span {
                            width: 9px;
                            height: 9px;
                            border-radius: 50%;
                            background: #4caf50;
                        }

                        .clinicHeroWorkspacePanel {
                            position: absolute;
                            left: -26px;
                            top: 28px;
                            width: 176px;
                            padding: 12px;
                            border: 1px solid rgba(255, 255, 255, 0.54);
                            border-radius: 10px;
                            background: rgba(255, 255, 255, 0.94);
                            color: var(--navy);
                            box-shadow: 0 18px 36px rgba(0, 20, 45, 0.32);
                            animation: clinicHeroFloat 5s ease-in-out infinite;
                        }

                        .clinicHeroWorkspaceKicker {
                            color: #62748a;
                            font-size: 0.62rem;
                            font-weight: 800;
                            letter-spacing: 0.07em;
                            text-transform: uppercase;
                        }

                        .clinicHeroWorkspaceTitle {
                            margin-top: 4px;
                            font-size: 0.8rem;
                            font-weight: 800;
                        }

                        .clinicHeroWorkspaceStatus {
                            display: flex;
                            align-items: center;
                            gap: 6px;
                            margin-top: 9px;
                            color: #2e7d32;
                            font-size: 0.7rem;
                            font-weight: 700;
                        }

                        .clinicHeroWorkspaceStatus span {
                            width: 7px;
                            height: 7px;
                            border-radius: 50%;
                            background: #4caf50;
                        }

                        .clinicHeroCarePlanPanel,
                        .clinicHeroMonitoringPanel {
                            position: absolute;
                            display: flex;
                            align-items: center;
                            gap: 9px;
                            padding: 10px 12px;
                            border: 1px solid rgba(255, 255, 255, 0.54);
                            border-radius: 10px;
                            background: rgba(255, 255, 255, 0.94);
                            color: var(--navy);
                            box-shadow: 0 18px 36px rgba(0, 20, 45, 0.32);
                        }

                        .clinicHeroCarePlanPanel {
                            right: -20px;
                            top: 58px;
                            animation: clinicHeroFloat 5.6s ease-in-out 0.5s infinite;
                        }

                        .clinicHeroMonitoringPanel {
                            left: 30px;
                            bottom: 10px;
                            animation: clinicHeroFloat 5.4s ease-in-out 1s infinite;
                        }

                        .clinicHeroOverlayIcon {
                            width: 28px;
                            height: 28px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border-radius: 8px;
                            background: #e6f1fb;
                            color: #0057a8;
                        }

                        .clinicHeroOverlayIcon.green {
                            background: #e8f5e9;
                            color: #2e7d32;
                        }

                        .clinicHeroOverlayText small,
                        .clinicHeroOverlayText strong {
                            display: block;
                        }

                        .clinicHeroOverlayText small {
                            color: #62748a;
                            font-size: 0.62rem;
                            font-weight: 700;
                        }

                        .clinicHeroOverlayText strong {
                            margin-top: 2px;
                            font-size: 0.72rem;
                        }

                        @keyframes clinicHeroFloat {
                            0%, 100% { transform: translateY(0); }
                            50% { transform: translateY(-8px); }
                        }

                        .clinicHeroCopy::before,
                        

                        .clinicBadge,
                        .clinicAddonPill {
                            display: inline-flex;
                            align-items: center;
                            gap: 8px;
                            background: rgba(255, 255, 255, 0.12);
                            border: 1px solid rgba(255, 255, 255, 0.18);
                            color: rgba(255, 255, 255, 0.82);
                            border-radius: 999px;
                            padding: 7px 14px;
                            font-size: ${LANDING_SHARED_TYPOGRAPHY.smallTextDesktop};
                            font-weight: 600;
                            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif !important;
                            letter-spacing: 0.05em;
                            text-transform: uppercase;
                        }

                        .clinicHeroTitle,
                        .clinicSectionTitle,
                        .clinicAddonTitle,
                        .clinicFinalTitle {
                            font-family: 'DM Serif Display', serif !important;
                            letter-spacing: -0.02em;
                        }

                        .clinicPlanTitle {
                            font-family: 'DM Serif Display', serif !important;
                            color: #FFFFFF !important;
                            spacing: 0.05em;
                            letter-spacing: 0.05em;
                        }

                        .clinicHeroTitle {
                            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif !important;
                            font-size: ${LANDING_SHARED_TYPOGRAPHY.heroTitleDesktop};
                            font-weight: 800;
                            line-height: 1.15;
                            margin: 16px 0 12px;
                            max-width: 620px;
                        }

                        .clinicHeroTitle em {
                            color: #fb741a;
                            font-style: normal;
                        }

                        .clinicHeroSubtitle,
                        .clinicSectionSubtitle,
                        .clinicAddonText,
                        .clinicFinalText {
                            font-size: ${LANDING_SHARED_TYPOGRAPHY.sectionBodyDesktop};
                            line-height: 1.6;
                        }

                        .clinicHeroSubtitle,
                        .clinicFinalText {
                            color: rgba(255, 255, 255, 0.75);
                            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif !important;
                            font-size: ${LANDING_SHARED_TYPOGRAPHY.heroBodyDesktop};
                            max-width: 470px;
                        }

                        .clinicHeroWorkflow {
                            display: flex;
                            flex-wrap: wrap;
                            align-items: center;
                            gap: 7px;
                            margin: 0 0 20px;
                            color: rgba(255, 255, 255, 0.84);
                            font-size: 0.78rem;
                            font-weight: 700;
                        }

                        .clinicHeroWorkflow span {
                            display: inline-flex;
                            align-items: center;
                            padding: 5px 8px;
                            border: 1px solid rgba(255, 255, 255, 0.2);
                            border-radius: 999px;
                            background: rgba(255, 255, 255, 0.1);
                        }

                        .clinicHeroWorkflow b {
                            color: #fb741a;
                            font-size: 0.9rem;
                        }

                        .clinicButtonRow {
                            display: flex;
                            flex-wrap: wrap;
                            gap: ${LANDING_SHARED_BUTTONS.desktopGap}px;
                            margin: 16px 0 24px;
                        }

                        .clinicLanding .clinicPrimaryButton,
                        .clinicLanding .clinicSecondaryButton {
                            border-radius: 16px;
                            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif !important;
                            font-size: ${LANDING_SHARED_BUTTONS.desktopFontSize};
                            font-weight: 700;
                            text-transform: none;
                            padding: ${LANDING_SHARED_BUTTONS.desktopPadding};
                            box-shadow: none;
                            transition: transform 0.18s, background 0.18s, box-shadow 0.18s;
                        }

                        @media (min-width: 1200px) and (max-width: 1499px) {
                            .clinicLanding .clinicPrimaryButton,
                            .clinicLanding .clinicSecondaryButton {
                                padding: ${LANDING_SHARED_BUTTONS.laptopPadding};
                                font-size: ${LANDING_SHARED_BUTTONS.desktopFontSize};
                            }
                        }

                        @media (max-width: 959px) {
                            .clinicButtonRow {
                                width: 100%;
                            }

                            .clinicProofList {
                                flex-wrap: wrap;
                                justify-content: flex-start;
                            }

                            .clinicLanding .clinicPrimaryButton,
                            .clinicLanding .clinicSecondaryButton {
                                flex: 1;
                                min-width: 0;
                                width: auto;
                                font-size: ${LANDING_SHARED_BUTTONS.mobileFontSize};
                            }
                        }

                        @media (min-width: 600px) and (max-width: 959px) {
                            .clinicLanding .clinicPrimaryButton,
                            .clinicLanding .clinicSecondaryButton {
                                padding: 11px 14px;
                            }
                        }

                        @media (max-width: 599px) {
                            .clinicButtonRow {
                                width: 100%;
                                gap: 8px;
                            }

                            .clinicLanding .clinicPrimaryButton,
                            .clinicLanding .clinicSecondaryButton {
                                padding: 9px 10px;
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.smallTextMobile};
                                flex: 1;
                                min-width: 0;
                                width: auto;
                            }
                        }

                        .clinicLanding .clinicPrimaryButton {
                            background: var(--orange);
                            color: #fff;
                        }

                        .clinicLanding .clinicPrimaryButton:hover {
                            background: #ff8c3d;
                            transform: translateY(-2px);
                            box-shadow: 0 10px 24px rgba(251, 116, 26, 0.34);
                        }

                        .clinicLanding .clinicSecondaryButton {
                            background: rgba(255, 255, 255, 0.12);
                            border: 1px solid rgba(255, 255, 255, 0.25);
                            color: #fff;
                        }

                        .clinicLanding .clinicSecondaryButton:hover {
                            background: rgba(255, 255, 255, 0.18);
                            transform: translateY(-2px);
                            box-shadow: 0 12px 40px rgba(0, 40, 90, 0.18);
                        }

                        .clinicPlatformWorkflow {
                            width: 100%;
                            padding: 72px 6% 64px;
                            background: #001e3c;
                            position: relative;
                        }

                        .clinicPlatformWorkflowInner {
                            max-width: 1190px;
                            margin: 0 auto;
                        }

                        .clinicPlatformWorkflowHeader {
                            text-align: center;
                            color: #fff;
                            margin-bottom: 48px;
                        }

                        .clinicPlatformWorkflowHeader h2 {
                            margin: 0 0 14px;
                            color: #fff;
                            font-size: clamp(2rem, 3.5vw, 3.2rem);
                            font-weight: 800;
                            line-height: 1.12;
                        }

                        .clinicPlatformWorkflowHeader h2 span {
                            color: var(--orange);
                        }

                        .clinicPlatformWorkflowHeader p {
                            max-width: 880px;
                            margin: 0 auto;
                            color: rgba(255, 255, 255, 0.78);
                            font-size: 1.08rem;
                            line-height: 1.55;
                        }

                        .clinicPlatformWorkflowGrid {
                            display: grid;
                            grid-template-columns: repeat(5, minmax(0, 1fr));
                            gap: 12px;
                        }

                        .clinicPlatformStep {
                            display: flex;
                            flex-direction: column;
                            min-height: 420px;
                            padding: 28px 18px 18px;
                            border: 1px solid rgba(163, 196, 230, 0.26);
                            border-radius: 12px;
                            background: rgba(0, 20, 45, 0.34);
                            color: #fff;
                        }

                        .clinicPlatformStep.featured {
                            background: rgba(255, 255, 255, 0.11);
                            border-color: rgba(251, 116, 26, 0.62);
                        }

                        .clinicPlatformStage {
                            color: var(--orange);
                            font-size: 0.7rem;
                            font-weight: 800;
                            letter-spacing: 0.14em;
                            text-transform: uppercase;
                        }

                        .clinicPlatformIcon {
                            width: 42px;
                            height: 42px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin: 16px 0;
                            border-radius: 8px;
                            background: rgba(251, 116, 26, 0.13);
                            color: var(--orange);
                        }

                        .clinicPlatformIcon img {
                            width: 26px;
                            height: 26px;
                            object-fit: contain;
                            filter: brightness(0) invert(1);
                            opacity: 0.72;
                        }

                        .clinicPlatformStep h3 {
                            margin: 0;
                            color: #f3f7fd;
                            font-size: 1.25rem;
                            font-weight: 800;
                            line-height: 1.28;
                        }

                        .clinicPlatformStep p {
                            margin: 16px 0 20px;
                            color: #aabed6;
                            font-size: 0.86rem;
                            line-height: 1.7;
                        }

                        .clinicPlatformOutcome {
                            width: calc(100% + 36px);
                            box-sizing: border-box;
                            margin: auto -18px -18px;
                            padding: 14px;
                            border: 1px solid var(--orange);
                            border-radius: 8px;
                            background: rgba(31, 78, 121, 0.76);
                            min-height: 130px;
                        }

                        .clinicPlatformOutcome strong {
                            display: block;
                            color: #fff;
                            font-size: 1.18rem;
                            font-weight: 800;
                            line-height: 1.25;
                        }

                        .clinicPlatformOutcomeHighlight {
                            color: var(--orange);
                        }

                        .clinicPlatformOutcome small {
                            display: block;
                            margin-top: 12px;
                            color: #aabed6;
                            font-size: 0.7rem;
                            line-height: 1.45;
                        }

                        .clinicSavingsTeaser {
                            padding: 58px 0;
                            background: linear-gradient(135deg, #003d72 0%, #002d55 100%);
                            overflow: hidden;
                        }

                        .clinicSavingsTeaserInner {
                            width: 100%;
                            max-width: 960px;
                            margin: 0 auto;
                            padding: 0 40px;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 28px;
                            position: relative;
                        }

                        .clinicSavingsTeaserCopy {
                            display: flex;
                            align-items: center;
                            gap: 14px;
                        }

                        .clinicSavingsTeaserIcon {
                            width: 48px;
                            height: 48px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            flex-shrink: 0;
                            border-radius: 8px;
                            background: var(--orange);
                            color: #fff;
                            box-shadow: 0 8px 22px rgba(251, 116, 26, 0.34);
                        }

                        .clinicSavingsTeaserEyebrow {
                            margin-bottom: 5px;
                            color: #ffb37c;
                            font-size: 0.7rem;
                            font-weight: 800;
                            letter-spacing: 0.08em;
                            text-transform: uppercase;
                        }

                        .clinicSavingsTeaserTitle {
                            margin: 0 0 4px;
                            color: #fff;
                            font-size: 1.35rem;
                            font-weight: 800;
                        }

                        .clinicSavingsTeaserText {
                            margin: 0;
                            color: rgba(255, 255, 255, 0.76);
                            font-size: 0.95rem;
                            line-height: 1.5;
                        }

                        .clinicSavingsTeaser .clinicPrimaryButton {
                            min-width: 178px;
                            padding: 13px 20px;
                            font-size: 0.95rem;
                            box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
                        }

                        .clinicProofList,
                        .clinicLogoStrip,
                        .clinicTrustStrip,
                        .clinicChipRow,
                        .clinicPlanFeatures,
                        .clinicFaqList {
                            display: flex;
                            flex-wrap: wrap;
                        }

                        .clinicProofList {
                            width: 100%;
                            grid-column: 1 / -1;
                            flex-wrap: nowrap;
                            gap: 12px;
                            margin: 0;
                            padding: 0;
                            list-style: none;
                            justify-content: space-between;
                        }

                        .clinicProofItem {
                            display: inline-flex;
                            align-items: center;
                            gap: 8px;
                            color: rgba(255, 255, 255, 0.74);
                            font-size: 0.85rem;
                        }

                        .clinicLogoStrip {
                            align-items: center;
                            gap: 14px;
                            margin-top: 22px;
                            justify-content: center;
                        }

                        .clinicLogoStrip img {
                            display: block;
                            max-height: 36px;
                            width: auto;
                            opacity: 0.94;
                        }

                        .clinicShowcasePanel,
                        .clinicMockPanel,
                        .clinicVideoPanel {
                            background: rgba(255, 255, 255, 0.62);
                            border: 1px solid rgba(255, 255, 255, 0.38);
                            border-radius: 30px;
                            box-shadow: 0 18px 40px rgba(0, 45, 85, 0.08);
                            padding: 22px;
                            backdrop-filter: blur(12px);
                        }

                        .clinicHero .clinicShowcasePanel {
                            background: rgba(255, 255, 255, 0.1);
                            border: 1px solid rgba(255, 255, 255, 0.16);
                            box-shadow: none;
                        }

                        .clinicShowcaseFrame {
                            min-height: 420px;
                            background: linear-gradient(145deg, #f4f7fb, #dfe7f0);
                            border-radius: 24px;
                            overflow: hidden;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border: 1px solid rgba(0, 45, 85, 0.08);
                        }

                        .clinicShowcaseImage,
                        .clinicShowcaseVideo,
                        .clinicStoryImage {
                            display: block;
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                        }

                        .clinicShowcaseVideo {
                            background: #001e3c;
                        }

                        .clinicShowcaseUnavailable {
                            width: 100%;
                            height: 100%;
                            position: relative;
                        }

                        .clinicShowcaseUnavailableNote {
                            position: absolute;
                            inset: auto 18px 18px 18px;
                            background: rgba(0, 45, 85, 0.78);
                            color: #fff;
                            border-radius: 16px;
                            padding: 12px 14px;
                            font-size: 0.84rem;
                            line-height: 1.5;
                        }

                        .clinicChipRow {
                            gap: 10px;
                            margin: 18px 0 0;
                        }

                        .clinicChipButton {
                            background: rgba(0, 45, 85, 0.08);
                            color: var(--navy);
                            border: 1px solid rgba(0, 45, 85, 0.12);
                            border-radius: 999px;
                            padding: 10px 14px;
                            font-size: 0.84rem;
                            font-weight: 700;
                            cursor: pointer;
                            transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
                            box-shadow: none;
                        }

                        .clinicChipButton:hover,
                        .clinicChipButton.active {
                            transform: translateY(-1px);
                            background: var(--navy);
                            color: #fff;
                            box-shadow: 0 10px 24px rgba(0, 45, 85, 0.14);
                        }

                        .clinicShowcaseMeta {
                            margin-top: 18px;
                        }

                        .clinicEyebrow,
                        .clinicSectionEyebrow,
                        .clinicPlanStage {
                            font-size: 0.76rem;
                            font-weight: 700;
                            letter-spacing: 0.09em;
                            text-transform: uppercase;
                        }

                        .clinicEyebrow,
                        .clinicSectionEyebrow,
                        .clinicFaqButton,
                        .clinicPlanStage {
                            color: var(--orange);
                        }

                        .clinicShowcaseTitle {
                            color: var(--navy);
                            font-size: 1.5rem;
                            line-height: 1.25;
                            margin: 8px 0 10px;
                            font-weight: 700;
                        }

                        .clinicShowcaseText,
                        .clinicFeatureBody,
                        .clinicValueBody,
                        .clinicCardBody,
                        .clinicFaqAnswer {
                            color: var(--muted);
                            line-height: 1.65;
                        }

                        .clinicTrustStrip {
                            background: var(--offWhite);
                            border-top: 1px solid var(--border);
                            border-bottom: 1px solid var(--border);
                            padding: 26px 0;
                            margin: 0;
                        }

                        .clinicTrustStripInner {
                            display: flex;
                            flex-wrap: wrap;
                            align-items: center;
                            justify-content: center;
                            gap: 18px 28px;
                        }

                        .clinicTrustPill {
                            display: inline-flex;
                            align-items: center;
                            gap: 10px;
                            color: var(--slate);
                            font-size: 0.83rem;
                            font-weight: 600;
                        }

                        .clinicTrustBadge {
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                            min-width: 52px;
                            padding: 4px 10px;
                            border-radius: 4px;
                            background: var(--navy);
                            color: #fff;
                            font-size: 0.63rem;
                            font-weight: 700;
                            letter-spacing: 0.06em;
                            text-transform: uppercase;
                        }

                        .clinicSection,
                        .clinicPlansSection,
                        .clinicFinalCta {
                            margin-top: 0;
                            border-radius: 0;
                        }

                        .clinicSection,
                        .clinicPlansSection {
                            background: transparent;
                            border: none;
                            padding: 80px 0;
                        }

                        .clinicSectionMuted {
                            background: var(--offWhite);
                            border-radius: 0;
                        }

                        .clinicSectionMuted::before {
                            content: '';
                            position: absolute;
                            inset: 0;
                            background:
                                radial-gradient(circle at 12% 0%, rgba(255, 255, 255, 0.78), transparent 22%),
                                linear-gradient(180deg, rgba(248, 249, 251, 0.84), rgba(237, 242, 247, 0.74));
                            pointer-events: none;
                        }

                        .clinicSectionMuted > * {
                            position: relative;
                            z-index: 1;
                        }

                        .clinicSectionHeader {
                            max-width: 680px;
                            margin-bottom: 48px;
                        }

                        .clinicExerciseHeader {
                            text-align: center;
                            margin-left: auto;
                            margin-right: auto;
                        }

                        .clinicExerciseHeader .clinicSectionDivider {
                            margin-left: auto;
                            margin-right: auto;
                        }

                        .clinicExerciseHeader .clinicSectionSubtitle {
                            margin-left: auto;
                            margin-right: auto;
                        }

                        .clinicSectionDivider {
                            width: 48px;
                            height: 3px;
                            background: var(--orange);
                            border-radius: 999px;
                            margin: 14px 0 22px;
                        }

                        .clinicSectionTitle {
                            color: var(--navy);
                            font-size: clamp(1.92rem, 3.6vw, 2.76rem);
                            line-height: 1.12;
                            margin: 10px 0 14px;
                        }

                        .clinicSectionSubtitle {
                            color: var(--muted);
                            font-size: ${LANDING_SHARED_TYPOGRAPHY.sectionBodyDesktop};
                            max-width: 620px;
                            margin: 0;
                        }

                        .clinicFeatureList,
                        .clinicValueGrid,
                        .clinicAdminGrid,
                        .clinicStoryGrid,
                        .clinicPlansGrid {
                            display: grid;
                            gap: 18px;
                        }

                        .clinicModernLayout {
                            display: grid;
                            grid-template-columns: minmax(320px, 0.95fr) minmax(0, 1.05fr);
                            gap: 32px;
                            align-items: start;
                        }

                        .clinicFeatureList {
                            grid-template-columns: 1fr;
                        }

                        .clinicFeatureItem,
                        .clinicMockCard,
                        .clinicValueCard,
                        .clinicAdminCard,
                        .clinicStoryCard,
                        .clinicPlanCard,
                        .clinicAddonBanner {
                            border-radius: 22px;
                        }

                        .clinicFeatureItem,
                        .clinicAdminCard,
                        .clinicStoryCard,
                        .clinicPlanCard {
                            background: rgba(255, 255, 255, 0.74);
                            border: 1px solid rgba(255, 255, 255, 0.44);
                            box-shadow: 0 12px 30px rgba(0, 45, 85, 0.05);
                        }

                        .clinicFeatureItem {
                            display: flex;
                            gap: 14px;
                            padding: 18px;
                        }

                        .clinicFeatureItem:nth-child(odd),
                        .clinicValueCard:nth-child(odd),
                        .clinicAdminCard:nth-child(odd) {
                            background: rgba(255, 255, 255, 0.6);
                        }

                        .clinicFeatureCheck {
                            width: 28px;
                            height: 28px;
                            border-radius: 50%;
                            background: rgba(76, 175, 80, 0.14);
                            color: #2e7d32;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            flex-shrink: 0;
                            margin-top: 2px;
                        }

                        .clinicFeatureTitle,
                        .clinicValueTitle,
                        .clinicCardTitle,
                        .clinicPlanTitle,
                        .clinicAddonTitle {
                            color: var(--navy);
                            margin: 0 0 8px;
                        }

                        .clinicFeatureTitle,
                        .clinicCardTitle {
                            font-size: clamp(0.95rem, 1.1vw, 1rem);
                            font-weight: 700;
                        }

                        .clinicMockPanel,
                        .clinicVideoPanel {
                            background: linear-gradient(145deg, rgba(238, 243, 249, 0.78), rgba(219, 229, 239, 0.74));
                            border: 1px solid rgba(255, 255, 255, 0.42);
                        }

                        .clinicMockCard,
                        .clinicVideoSubcard {
                            background: rgba(255, 255, 255, 0.82);
                            border-radius: 18px;
                            padding: 18px;
                            box-shadow: 0 10px 24px rgba(0, 45, 85, 0.05);
                        }

                        .clinicMockCard + .clinicMockCard,
                        .clinicVideoSubcard {
                            margin-top: 14px;
                        }

                        .clinicMockRow {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 12px;
                            margin-bottom: 10px;
                        }

                        .clinicMockLabel {
                            color: var(--muted);
                            font-size: 0.72rem;
                            font-weight: 700;
                            letter-spacing: 0.07em;
                            text-transform: uppercase;
                        }

                        .clinicMockStatus {
                            border-radius: 999px;
                            padding: 4px 10px;
                            font-size: 0.72rem;
                            font-weight: 700;
                        }

                        .clinicStatusGreen {
                            background: rgba(76, 175, 80, 0.16);
                            color: #2e7d32;
                        }

                        .clinicStatusBlue {
                            background: rgba(25, 118, 210, 0.14);
                            color: #1565c0;
                        }

                        .clinicStatusOrange {
                            background: rgba(251, 116, 26, 0.15);
                            color: #dd5a06;
                        }

                        .clinicValueGrid,
                        .clinicAdminGrid,
                        .clinicStoryGrid,
                        .clinicPlansGrid {
                            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                        }

                        .clinicModernLayout .clinicStoryGrid {
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                        }

                        .clinicValueCard,
                        .clinicAdminCard,
                        .clinicPlanCard {
                            padding: 24px;
                        }

                        .clinicValueCard {
                            min-height: 230px;
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                        }

                        .clinicValueKicker,
                        .clinicPlanBadge {
                            display: inline-flex;
                            align-items: center;
                            border-radius: 999px;
                            padding: 4px 10px;
                            font-size: 0.72rem;
                            font-weight: 700;
                            letter-spacing: 0.05em;
                            text-transform: uppercase;
                        }

                        .clinicValueKicker {
                            background: rgba(0, 45, 85, 0.08);
                            color: var(--navy);
                            margin-bottom: 16px;
                        }

                        .clinicValueIcon {
                            width: 46px;
                            height: 46px;
                            border-radius: 14px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin-bottom: 18px;
                            color: var(--navy);
                        }

                        .clinicValueIcon.blue {
                            background: #e6f1fb;
                        }

                        .clinicValueIcon.orange {
                            background: #fff0e6;
                        }

                        .clinicValueIcon.green {
                            background: #e8f5e9;
                            color: #2e7d32;
                        }

                        .clinicValueIcon.teal {
                            background: #e0f7f4;
                        }

                        .clinicValueIcon.purple {
                            background: #f3e8ff;
                        }

                        .clinicValueIcon.grey {
                            background: #f0f2f5;
                        }

                        .clinicStoryCard {
                            overflow: hidden;
                            background: rgba(255, 255, 255, 0.68);
                        }

                        .clinicStoryImageWrap {
                            aspect-ratio: 16 / 10;
                            overflow: hidden;
                        }

                        .clinicStoryBody {
                            padding: 20px;
                        }

                        .clinicVideoHighlight {
                            background: linear-gradient(135deg, var(--navy) 0%, #00407a 100%);
                            color: #fff;
                            padding: 48px;
                            border-radius: 28px;
                            box-shadow: var(--shadowMd);
                            overflow: hidden;
                            gap: 48px;
                            align-items: center;
                        }

                        .clinicVideoHighlight::before {
                            content: '';
                            position: absolute;
                            inset: auto -8% -26% auto;
                            width: 340px;
                            height: 340px;
                            border-radius: 50%;
                            background: radial-gradient(circle, rgba(251, 116, 26, 0.26), rgba(251, 116, 26, 0));
                            pointer-events: none;
                        }

                        .clinicVideoList,
                        .clinicPlanFeatures {
                            list-style: none;
                            margin: 18px 0 0;
                            padding: 0;
                            gap: 10px;
                            flex-direction: column;
                        }

                        .clinicVideoList li,
                        .clinicPlanFeatures li {
                            display: flex;
                            gap: 10px;
                            align-items: flex-start;
                            line-height: 1.55;
                        }

                        .clinicVideoList li {
                            color: rgba(255, 255, 255, 0.76);
                        }

                        .clinicVideoPanel {
                            background: rgba(255, 255, 255, 0.08);
                            border-color: rgba(255, 255, 255, 0.12);
                            color: rgba(255, 255, 255, 0.86);
                            padding: 24px;
                        }

                        .clinicVideoScreen {
                            aspect-ratio: 16 / 9;
                            border-radius: 14px;
                            background: rgba(0, 0, 0, 0.4);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin-bottom: 16px;
                        }

                        .clinicVideoPlayButton {
                            width: 52px;
                            height: 52px;
                            border-radius: 50%;
                            background: rgba(251, 116, 26, 0.85);
                            color: #fff;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 18px;
                            font-weight: 700;
                        }

                        .clinicVideoControls {
                            display: flex;
                            align-items: center;
                            gap: 10px;
                        }

                        .clinicVideoLiveDot {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            background: #ef5350;
                            flex-shrink: 0;
                        }

                        .clinicVideoControlLabel {
                            font-size: 0.72rem;
                            color: rgba(255, 255, 255, 0.5);
                        }

                        .clinicVideoActions {
                            margin-left: auto;
                            display: flex;
                            gap: 8px;
                        }

                        .clinicVideoAction {
                            font-size: 0.68rem;
                            font-weight: 600;
                            padding: 4px 12px;
                            border-radius: 6px;
                            background: rgba(255, 255, 255, 0.1);
                            color: rgba(255, 255, 255, 0.7);
                            border: none;
                        }

                        .clinicVideoSubcard {
                            padding: 16px;
                            background: rgba(255, 255, 255, 0.07);
                            border-radius: 14px;
                            box-shadow: none;
                        }

                        .clinicAdminCard {
                            border-top: 3px solid rgba(0, 45, 85, 0.72);
                        }

                        .clinicAddonBanner {
                            background: linear-gradient(135deg, #1b2338 0%, #16213e 100%);
                            color: #fff;
                            display: flex;
                            flex-wrap: wrap;
                            gap: 24px;
                            justify-content: space-between;
                            padding: 32px;
                            border: 1px solid rgba(255, 255, 255, 0.08);
                        }

                        .clinicAddonPill {
                            background: rgba(251, 116, 26, 0.2);
                            border-color: rgba(251, 116, 26, 0.4);
                            color: #fb9f5a;
                        }

                        .clinicAddonText {
                            color: rgba(255, 255, 255, 0.6);
                            max-width: 560px;
                        }

                        .clinicAddonMark {
                            font-family: 'DM Serif Display', serif;
                            color: rgba(251, 116, 26, 0.4);
                            font-size: clamp(2rem, 6vw, 4rem);
                            align-self: center;
                        }

                        .clinicPlansSection {
                            background: linear-gradient(145deg, rgba(0, 45, 85, 0.94), rgba(8, 60, 106, 0.9));
                            border: none;
                            color: #fff;
                            padding: 80px 0;
                            box-shadow: none;
                        }

                        .clinicPlansSection .clinicSectionTitle,
                        .clinicPlansSection .clinicSectionSubtitle,
                        .clinicPlansSection .clinicSectionEyebrow {
                            color: #fff;
                        }

                        .clinicPlansSection .clinicSectionSubtitle {
                            color: rgba(255, 255, 255, 0.7);
                        }

                        .clinicPlanCard {
                            position: relative;
                            background: rgba(255, 255, 255, 0.05);
                            border-color: rgba(255, 255, 255, 0.1);
                            color: #fff;
                            box-shadow: none;
                        }

                        .clinicPlanCard.featured {
                            background: rgba(251, 116, 26, 0.12);
                            border-color: rgba(251, 116, 26, 0.42);
                        }

                        .clinicPlanBadge {
                            position: absolute;
                            top: -14px;
                            left: 24px;
                            background: var(--orange);
                            color: #fff;
                        }

                        .clinicPlanTitle {
                            font-family: 'DM Serif Display', serif;
                            font-size: 1.35rem;
                        }

                        .clinicPlanTagline,
                        .clinicPlanFeatures li {
                            color: rgba(255, 255, 255, 0.78);
                        }

                        .clinicPlanPrice {
                            display: flex;
                            align-items: baseline;
                            gap: 6px;
                            margin: 18px 0 22px;
                            padding-bottom: 18px;
                            border-bottom: 1px solid rgba(255, 255, 255, 0.12);
                        }

                        .clinicPlanAmount {
                            font-size: 1.55rem;
                            font-weight: 700;
                        }

                        .clinicFaqSection {
                            background: #fff;
                        }

                        .clinicFaqList {
                            flex-direction: column;
                            gap: 0;
                            margin-top: 26px;
                        }

                        .clinicFaqItem {
                            background: transparent;
                            border: none;
                            border-bottom: 1px solid var(--border);
                            border-radius: 0;
                            padding: 0;
                            box-shadow: none;
                            backdrop-filter: none;
                        }

                        .clinicLanding .clinicFaqButton {
                            width: 100%;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            text-align: left;
                            color: var(--navy);
                            font-size: 0.98rem;
                            font-weight: 700;
                            text-transform: none;
                            padding: 20px 0;
                        }

                        .clinicFaqAnswer {
                            padding: 0 0 18px;
                        }

                        .clinicFinalCta {
                            background: linear-gradient(135deg, var(--navy) 0%, #003d72 100%);
                            padding: 100px 0;
                            position: relative;
                            overflow: hidden;
                            text-align: center;
                            box-shadow: none;
                        }

                        .clinicFinalTitle {
                            color: #fff;
                            font-size: clamp(1.8rem, 3.2vw, 2.8rem);
                            margin: 0 0 14px;
                        }

                        .clinicFinalText {
                            max-width: 620px;
                            margin: 0 auto 28px;
                        }

                        @media (min-width: 1200px) {
                            .clinicHero {
                                min-height: ${LANDING_SHARED_LAYOUT.heroDesktopMinHeight}px;
                                display: flex;
                                align-items: center;
                            }

                            .clinicHeroContent,
                            .clinicSectionContent,
                            .clinicPlansContent,
                            .clinicFinalContent,
                            .clinicTrustStripInner {
                                max-width: ${LANDING_SHARED_LAYOUT.heroDesktopMaxWidth}px;
                                padding: 0 6%;
                            }

                            .clinicStatsOuter {
                                padding: 0 6%;
                            }

                            .clinicStatValue {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statValueDesktop};
                            }

                            .clinicStatLabel {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statLabelDesktop};
                                line-height: 1.24;
                            }

                            .clinicHeroTitle {
                                font-size: clamp(1.9rem, 3vw, 2.85rem);
                                margin: 18px 0 14px;
                            }

                            .clinicHeroSubtitle,
                            .clinicFinalText {
                                font-size: 1rem;
                                max-width: 520px;
                            }

                            .clinicSection,
                            .clinicPlansSection {
                                padding: 80px 0;
                            }

                            .clinicSectionHeader {
                                margin-bottom: 40px;
                            }

                            .clinicSectionTitle {
                                font-size: clamp(2.04rem, 3.12vw, 2.82rem);
                            }

                            .clinicSectionSubtitle {
                                font-size: 1rem;
                                max-width: 660px;
                            }

                            .clinicShowcasePanel,
                            .clinicMockPanel,
                            .clinicVideoPanel {
                                padding: 20px;
                            }

                            .clinicShowcaseFrame {
                                min-height: 400px;
                            }

                            .clinicShowcaseTitle {
                                font-size: 1.4rem;
                            }

                            .clinicValueGrid,
                            .clinicAdminGrid,
                            .clinicStoryGrid,
                            .clinicPlansGrid {
                                grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                            }

                            .clinicValueCard,
                            .clinicAdminCard,
                            .clinicPlanCard {
                                padding: 22px;
                            }

                            .clinicVideoHighlight {
                                padding: 44px;
                                gap: 40px;
                            }
                        }

                        ${LANDING_LAPTOP_BAND} {
                            .clinicHero {
                                min-height: ${LANDING_SHARED_LAYOUT.heroDesktopMinHeight}px;
                            }

                            .clinicHeroContent,
                            .clinicSectionContent,
                            .clinicPlansContent,
                            .clinicFinalContent,
                            .clinicTrustStripInner {
                                max-width: ${LANDING_SHARED_LAYOUT.sectionLaptopMaxWidth}px;
                                padding: 0 ${LANDING_SHARED_LAYOUT.sectionLaptopPaddingX};
                            }

                            .clinicStatsOuter {
                                padding: 0 ${LANDING_SHARED_LAYOUT.sectionLaptopPaddingX};
                            }

                            .clinicStatValue {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statValueLaptop};
                            }

                            .clinicStatLabel {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statLabelLaptop};
                                line-height: 1.24;
                            }

                            .clinicSection,
                            .clinicPlansSection {
                                padding: 56px 0;
                            }

                            .clinicSectionHeader {
                                margin-bottom: 24px;
                            }

                            .clinicHeroTitle {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.heroTitleLaptop};
                                margin: 12px 0 9px;
                            }

                            .clinicHeroSubtitle,
                            .clinicFinalText {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.heroBodyLaptop};
                                max-width: 400px;
                            }

                            .clinicSectionTitle {
                                font-size: clamp(1.66rem, 2.34vw, 2.11rem);
                            }

                            .clinicSectionSubtitle {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.sectionBodyLaptop};
                                max-width: 460px;
                            }

                            .clinicShowcasePanel,
                            .clinicMockPanel,
                            .clinicVideoPanel {
                                padding: 12px;
                            }

                            .clinicShowcaseFrame {
                                min-height: 300px;
                            }

                            .clinicShowcaseTitle {
                                font-size: clamp(1rem, 1.2vw, 1.04rem);
                            }

                            .clinicValueGrid,
                            .clinicAdminGrid,
                            .clinicStoryGrid,
                            .clinicPlansGrid {
                                grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                            }

                            .clinicValueCard,
                            .clinicAdminCard,
                            .clinicPlanCard {
                                padding: 15px;
                            }

                            .clinicVideoHighlight {
                                padding: 28px;
                                gap: 20px;
                            }

                            .clinicButtonRow {
                                margin: 12px 0 20px;
                                gap: ${LANDING_SHARED_BUTTONS.laptopGap}px;
                            }

                            .clinicLanding .clinicPrimaryButton,
                            .clinicLanding .clinicSecondaryButton {
                                padding: ${LANDING_SHARED_BUTTONS.laptopPadding};
                                font-size: ${LANDING_SHARED_BUTTONS.laptopFontSize};
                            }

                            .clinicLogoStrip img {
                                max-height: 28px;
                            }
                        }

                        @media (min-width: 960px) and (max-width: 1350px) {
                            .clinicHero {
                                min-height: 628px;
                            }

                            .clinicHeroContent,
                            .clinicSectionContent,
                            .clinicPlansContent,
                            .clinicFinalContent,
                            .clinicTrustStripInner {
                                max-width: 1100px;
                                padding: 0 28px;
                            }

                            .clinicStatsOuter {
                                padding: 0 4.5%;
                            }

                            .clinicStatBlock {
                                padding: 24px 13px;
                            }

                            .clinicStatValue {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statValueTablet};
                            }

                            .clinicStatLabel {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statLabelTablet};
                            }

                            .clinicSection,
                            .clinicPlansSection {
                                padding: 68px 0;
                            }

                            .clinicSectionHeader {
                                margin-bottom: 36px;
                            }

                            .clinicHeroTitle {
                                font-size: clamp(1.75rem, 3.2vw, 2.6rem);
                                margin: 20px 0 16px;
                            }

                            .clinicHero {
                                padding: 65px 5% 65px;
                            }

                            .clinicHeroSubtitle,
                            .clinicFinalText {
                                font-size: clamp(0.95rem, 1.35vw, 1.05rem);
                                max-width: 480px;
                            }

                            .clinicButtonRow {
                                margin: 18px 0 28px;
                                gap: 12px;
                            }

                            .clinicLanding .clinicPrimaryButton,
                            .clinicLanding .clinicSecondaryButton {
                                padding: ${LANDING_SHARED_BUTTONS.laptopPadding};
                                font-size: ${LANDING_SHARED_BUTTONS.desktopFontSize};
                            }

                            .clinicShowcasePanel,
                            .clinicMockPanel,
                            .clinicVideoPanel {
                                padding: 18px;
                            }

                            .clinicShowcaseFrame {
                                min-height: 360px;
                            }

                            .clinicShowcaseTitle {
                                font-size: 1.35rem;
                            }

                            .clinicValueGrid,
                            .clinicAdminGrid,
                            .clinicStoryGrid,
                            .clinicPlansGrid {
                                grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
                            }

                            .clinicValueCard,
                            .clinicAdminCard,
                            .clinicPlanCard {
                                padding: 20px;
                            }

                            .clinicValueCard {
                                min-height: 220px;
                            }

                            .clinicVideoHighlight {
                                padding: 40px;
                                gap: 32px;
                            }

                            .clinicPlanTitle {
                                font-size: 1.35rem;
                            }

                            .clinicPlanAmount {
                                font-size: 1.55rem;
                            }

                            .clinicLogoStrip img {
                                max-height: 38px;
                            }
                        }

                        @media (min-width: 600px) and (max-width: 959px) {
                            .clinicLanding {
                                padding: 0 0 72px;
                            }

                            .clinicPlatformWorkflow {
                                padding: clamp(40px, 6vw, 56px) clamp(16px, 3vw, 20px);
                            }

                            .clinicPlatformWorkflowGrid {
                                grid-template-columns: repeat(2, minmax(0, 1fr));
                            }

                            .clinicPlatformStep {
                                min-height: 360px;
                            }

                            .clinicStatsOuter {
                                padding: 0 20px;
                            }

                            .clinicStatsBar {
                                flex-wrap: nowrap;
                            }

                            .clinicStatBlock {
                                flex: 1 1 25%;
                                border-right: 1px solid #dce8f7;
                                border-bottom: none;
                            }

                            .clinicStatBlock:nth-child(4n) {
                                border-right: none;
                            }

                            .clinicStatBlock:nth-last-child(-n + 4) {
                                border-bottom: none;
                            }

                            .clinicHeroContent,
                            .clinicFeatureLayout,
                            .clinicRemoteLayout,
                            .clinicVideoHighlight,
                            .clinicModernLayout {
                                grid-template-columns: 1fr;
                            }

                            .clinicHeroContent,
                            .clinicSectionContent,
                            .clinicPlansContent,
                            .clinicFinalContent,
                            .clinicTrustStripInner {
                                padding: 0 20px;
                            }

                            .clinicStatBlock {
                                padding: 24px 13px;
                            }

                            .clinicStatValue {
                                font-size: clamp(1.2rem, 1.5vw, 1.35rem);
                            }

                            .clinicStatLabel {
                                font-size: 0.76rem;
                            }

                            .clinicHeroCopy {
                                padding: 0;
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                text-align: center;
                            }

                            .clinicSection,
                            .clinicPlansSection {
                                padding: 56px 0;
                            }

                            .clinicSectionHeader {
                                margin-bottom: 32px;
                            }

                            .clinicShowcaseFrame {
                                min-height: 280px;
                            }

                            .clinicShowcasePanel,
                            .clinicMockPanel,
                            .clinicVideoPanel {
                                padding: 16px;
                            }

                            .clinicShowcaseTitle {
                                font-size: 1.2rem;
                            }

                            .clinicValueGrid,
                            .clinicAdminGrid,
                            .clinicPlansGrid {
                                grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                            }

                            .clinicModernLayout .clinicStoryGrid {
                                grid-template-columns: 1fr;
                            }

                            .clinicValueCard,
                            .clinicAdminCard,
                            .clinicPlanCard {
                                padding: 18px;
                            }

                            .clinicValueCard {
                                min-height: 200px;
                            }

                            .clinicFeatureItem {
                                padding: 16px;
                            }

                            .clinicVideoHighlight {
                                padding: 32px 24px;
                                gap: 24px;
                            }

                            .clinicFinalCta {
                                padding: 80px 0;
                            }

                            .clinicFinalTitle {
                                font-size: clamp(1.8rem, 3vw, 2.6rem);
                            }

                            .clinicPlanTitle {
                                font-size: 1.25rem;
                            }

                            .clinicPlanAmount {
                                font-size: 1.45rem;
                            }
                        }

                        @media (max-width: 599px) {
                            .clinicLanding {
                                padding: 0 0 56px;
                            }

                            .clinicPlatformWorkflow {
                                padding: clamp(28px, 9vw, 40px) clamp(12px, 4vw, 14px);
                            }

                            .clinicPlatformWorkflowHeader {
                                margin-bottom: 24px;
                            }

                            .clinicPlatformWorkflowHeader h2 {
                                font-size: clamp(1.55rem, 6.5vw, 1.95rem);
                            }

                            .clinicPlatformStep {
                                padding: 22px 14px 14px;
                            }

                            .clinicPlatformStep h3 {
                                font-size: 1.08rem;
                            }

                            .clinicPlatformStep p {
                                margin: 6px 0 14px;
                                font-size: 0.8rem;
                            }

                            .clinicPlatformOutcome {
                                width: calc(100% + 28px);
                                margin: auto -14px -14px;
                                min-height: 0;
                                padding: 12px;
                            }

                            .clinicPlatformOutcome strong {
                                font-size: 1rem;
                            }

                            .clinicPlatformWorkflowHeader p {
                                font-size: 0.95rem;
                            }

                            .clinicPlatformWorkflowGrid {
                                grid-template-columns: 1fr;
                            }

                            .clinicPlatformStep {
                                min-height: 0;
                            }

                            .clinicHero {
                                padding: 42px 14px 48px;
                            }

                            .clinicHeroContent {
                                gap: 24px;
                                grid-template-columns: 1fr;
                            }

                            .clinicHeroContent,
                            .clinicSectionContent,
                            .clinicPlansContent,
                            .clinicFinalContent,
                            .clinicTrustStripInner {
                                padding: 0 14px;
                            }

                            .clinicHeroTitle {
                                font-size: clamp(1.4rem, 6.5vw, 1.8rem);
                                margin: 14px 0 12px;
                            }

                            .clinicHeroSubtitle,
                            .clinicFinalText {
                                font-size: 0.88rem;
                                max-width: 100%;
                                line-height: 1.55;
                            }

                            .clinicHeroMedia {
                                width: min(100%, 360px);
                                margin: 0 auto;
                            }

                            .clinicHeroMediaFrame {
                                padding: 9px 9px 16px;
                                border-radius: 16px 16px 12px 12px;
                            }

                            .clinicHeroMediaFrame img {
                                border-radius: 9px;
                            }

                            .clinicHeroMediaFrame::after {
                                bottom: -8px;
                                height: 8px;
                                border-radius: 0 0 12px 12px;
                            }

                            .clinicHeroWorkspacePanel {
                                display: block;
                                left: 4px;
                                top: 12px;
                                width: 128px;
                                padding: 8px;
                            }

                            .clinicHeroMediaLabel,
                            .clinicHeroCarePlanPanel,
                            .clinicHeroMonitoringPanel {
                                display: flex;
                                padding: 7px 8px;
                                gap: 6px;
                            }

                            .clinicHeroMediaLabel {
                                right: 4px;
                                bottom: 16px;
                                font-size: 0.62rem;
                            }

                            .clinicHeroCarePlanPanel {
                                right: 4px;
                                top: 48px;
                            }

                            .clinicHeroMonitoringPanel {
                                left: 12px;
                                bottom: 12px;
                            }

                            .clinicHeroOverlayIcon {
                                width: 22px;
                                height: 22px;
                            }

                            .clinicHeroOverlayText small {
                                font-size: 0.56rem;
                            }

                            .clinicHeroOverlayText strong {
                                font-size: 0.64rem;
                            }

                            .clinicHeroWorkspaceKicker {
                                font-size: 0.54rem;
                            }

                            .clinicHeroWorkspaceTitle {
                                font-size: 0.68rem;
                            }

                            .clinicHeroWorkspaceStatus {
                                margin-top: 6px;
                                font-size: 0.6rem;
                            }

                            .clinicSection,
                            .clinicPlansSection {
                                padding: 36px 0;
                            }

                            .clinicSectionTitle {
                                font-size: clamp(1.48rem, 6.8vw, 2rem);
                            }

                            .clinicSectionSubtitle {
                                font-size: 0.88rem;
                            }

                            .clinicShowcasePanel,
                            .clinicMockPanel,
                            .clinicVideoPanel {
                                padding: 14px;
                            }

                            .clinicShowcaseFrame {
                                min-height: 210px;
                                border-radius: 18px;
                            }

                            .clinicShowcaseTitle {
                                font-size: 1.1rem;
                            }

                            .clinicChipRow {
                                gap: 8px;
                            }

                            .clinicChipButton {
                                width: 100%;
                                justify-content: center;
                                font-size: 0.8rem;
                                padding: 9px 12px;
                            }

                            .clinicButtonRow {
                                flex-direction: column;
                                align-items: stretch;
                                width: 100%;
                            }

                            .clinicLanding .clinicPrimaryButton,
                            .clinicLanding .clinicSecondaryButton {
                                width: 100%;
                                flex: 0 0 auto;
                                min-width: 0;
                                font-size: ${LANDING_SHARED_BUTTONS.mobileFontSize};
                                padding: 12px 16px;
                            }

                            .clinicProofList {
                                display: grid;
                                grid-template-columns: repeat(2, minmax(0, 1fr));
                                gap: 10px 12px;
                                margin-top: 2px;
                            }

                            .clinicProofItem {
                                align-items: flex-start;
                                font-size: 0.78rem;
                                line-height: 1.35;
                            }

                            .clinicStatsOuter {
                                padding: 0 14px;
                                margin-top: -10px;
                            }

                            .clinicStatBlock {
                                padding: 20px 14px;
                            }

                            .clinicStatValue {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statValueMobile};
                            }

                            .clinicStatLabel {
                                font-size: ${LANDING_SHARED_TYPOGRAPHY.statLabelMobile};
                            }

                            .clinicSavingsTeaser {
                                padding: 42px 0;
                            }

                            .clinicSavingsTeaserInner {
                                padding: 0 20px;
                                align-items: flex-start;
                                flex-direction: column;
                                gap: 18px;
                            }

                            .clinicSavingsTeaserCopy {
                                align-items: flex-start;
                            }

                            .clinicSavingsTeaser .clinicPrimaryButton {
                                width: 100%;
                            }

                            .clinicValueGrid,
                            .clinicAdminGrid,
                            .clinicStoryGrid,
                            .clinicPlansGrid {
                                grid-template-columns: 1fr;
                                gap: 14px;
                            }

                            .clinicValueCard,
                            .clinicAdminCard,
                            .clinicStoryCard,
                            .clinicPlanCard {
                                width: 100%;
                                padding: 14px;
                            }

                            .clinicValueCard {
                                min-height: auto;
                            }

                            .clinicVideoHighlight {
                                grid-template-columns: 1fr;
                                padding: 20px 14px;
                                gap: 14px;
                            }

                            .clinicVideoList {
                                margin-top: 14px;
                            }

                            .clinicFinalCta {
                                padding: 64px 0;
                            }

                            .clinicFinalTitle {
                                font-size: clamp(1.65rem, 8vw, 2.2rem);
                            }
                        }
                    `}</style>

                    <div className="clinicLanding">
                        <section className="clinicHero">
                            <div className="clinicHeroContent">
                                <div className="clinicHeroCopy">
                                    <h1 className="clinicHeroTitle">Run the whole patient journey in <em>one clinical platform.</em></h1>
                                    <p className="clinicHeroSubtitle">Digital intake, data-driven auto-journaling, adaptive exercise programs with clinician control built in, and a connected patient app for patient eduation, home exercise programs, remote monitoring, video consultations, booking, billing, bookkeeping and automated accounting.<br /><br />Replace fragmented tools, save time, avoid repetitive and double work with data-driven automations, and know patients before you see them.</p>
                                    {/*<div className="clinicHeroWorkflow" aria-label="VIRTUS clinical workflow">
                                        <span>Intake</span><b>→</b><span>Journal</span><b>→</b><span>Treatment</span><b>→</b><span>Follow-up</span>
                                    </div>*/}
                                    <div className="clinicButtonRow">
                                        <Button variant="contained" disableElevation className="clinicPrimaryButton" onClick={openSignupDialog}>
                                            {heroSignupLabel}
                                        </Button>
                                        <Button variant="contained" disableElevation className="clinicSecondaryButton" href="#contact">
                                            Book a demo
                                        </Button>
                                    </div>
                                    {/*<div className="clinicLogoStrip">
                                        <img src={CEMark} alt="CE mark" />
                                        <img src={GDPRcompliant_logoGrey} alt="GDPR compliant" />
                                        <img src={DanishHealth} alt="Danish Health Authority" />
                                    </div>*/}
                                </div>
                                <div className="clinicHeroMedia">
                                    <div className="clinicHeroMediaFrame">
                                        <img src={showcaseLaptop} alt="VIRTUS Clinic platform preview" />
                                        <div className="clinicHeroMediaLabel"><span />Real-time Patient dashboard </div>
                                    </div>
                                    <div className="clinicHeroWorkspacePanel">
                                        <div className="clinicHeroWorkspaceKicker">Today&apos;s workflow</div>
                                        <div className="clinicHeroWorkspaceTitle">Intake ready for review</div>
                                        <div className="clinicHeroWorkspaceStatus"><span />Journal draft created</div>
                                    </div>
                                    <div className="clinicHeroCarePlanPanel">
                                        <div className="clinicHeroOverlayIcon"><FitnessCenterIcon style={{ fontSize: 17 }} /></div>
                                        <div className="clinicHeroOverlayText">
                                            <small>Care plan</small>
                                            <strong>Phase 2 ready</strong>
                                        </div>
                                    </div>
                                    <div className="clinicHeroMonitoringPanel">
                                        <div className="clinicHeroOverlayIcon green"><AssessmentIcon style={{ fontSize: 17 }} /></div>
                                        <div className="clinicHeroOverlayText">
                                            <small>Patient monitoring</small>
                                            <strong>Progress on track</strong>
                                        </div>
                                    </div>
                                </div>
                                <ul className="clinicProofList">
                                    {HERO_PROOF_POINTS.map((point) => (
                                        <li key={point} className="clinicProofItem">
                                            <CheckCircleIcon style={{ fontSize: 18, color: '#81c784' }} />
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        <section className="clinicPlatformWorkflow">
                            <div className="clinicPlatformWorkflowInner">
                                <header className="clinicPlatformWorkflowHeader">
                                    <h2>All-in-one platform. <span>Every step automated.</span></h2>
                                    <p>From the moment a patient looks for care to the moment accounting is complete, VIRTUS keeps the clinic workflow connected.</p>
                                </header>
                                <div className="clinicPlatformWorkflowGrid">
                                    {PLATFORM_WORKFLOW_STEPS.map((step) => {
                                        const StepIcon = step.icon;

                                        return (
                                            <article key={step.stage} className={`clinicPlatformStep${step.featured ? ' featured' : ''}`}>
                                                <div className="clinicPlatformStage">{step.stage}</div>
                                                <div className="clinicPlatformIcon">
                                                    {step.image ? <img src={step.image} alt="" /> : step.emoji || <StepIcon style={{ fontSize: 24 }} />}
                                                </div>
                                                <Typography style={{ fontWeight: 'bold', fontSize: '1.25rem', color: '#FFFFFF' }}>{step.title}</Typography>
                                                <p>{step.body}</p>
                                                <div className="clinicPlatformOutcome">
                                                    <strong>{step.outcome} {step.outcomeHighlight && <span className="clinicPlatformOutcomeHighlight">{step.outcomeHighlight}</span>}</strong>
                                                    <small>{step.detail}</small>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>

                        {/*<div className="clinicTrustStrip">
                            <div className="clinicTrustStripInner">
                                {TRUST_BADGES.map((item) => (
                                    <div key={item.label} className="clinicTrustPill">
                                        <span className="clinicTrustBadge">{item.badge}</span>
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>*/}

                        <section className="clinicSection">
                            <div className="clinicSectionContent">
                                <div className="clinicFeatureLayout">
                                    <div>
                                        <div className="clinicSectionHeader">
                                            <div className="clinicSectionEyebrow">Digital intake and auto-journaling</div>
                            <Dialog
                                open={savingsCalculatorOpen}
                                onClose={() => setSavingsCalculatorOpen(false)}
                                maxWidth="md"
                                fullWidth
                                PaperProps={{ style: { borderRadius: 18, backgroundColor: '#f7faff' } }}
                            >
                                <DialogTitle style={{ paddingBottom: 0, color: '#002D55', position: 'relative' }}>
                                    <IconButton
                                        aria-label="Close savings calculator"
                                        onClick={() => setSavingsCalculatorOpen(false)}
                                        style={{ position: 'absolute', right: 8, top: 8, color: '#324158' }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 36 }}>
                                        <ReceiptIcon style={{ color: '#FF7F27' }} />
                                        <div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: '#002D55' }}>{savingsCalculatorStep === 0 ? 'How large is your clinic?' : 'What do you pay for today?'}</div>
                                        </div>
                                    </div>
                                    <Stepper activeStep={savingsCalculatorStep} alternativeLabel style={{ background: 'transparent', padding: '16px 0 0 0', paddingTop: 24 }} className={classes.signupStepper}>
                                        <Step completed={savingsCalculatorStep > 0}><StepLabel StepIconComponent={CustomStepIcon}>Clinic size</StepLabel></Step>
                                        <Step completed={savingsCalculatorStep > 1}><StepLabel StepIconComponent={CustomStepIcon}>Expenses</StepLabel></Step>
                                    </Stepper>
                                </DialogTitle>
                                <DialogContent dividers style={{ borderTop: '1px solid #dfe8f3', borderBottom: '1px solid #dfe8f3' }}>
                                    {savingsCalculatorStep === 0 && (
                                        <div>
                                            <Typography variant="body2" style={{ color: '#324158', marginBottom: 20 }}>
                                                Start with the number of clinician seats you need. Your first seat is included below; add seats as your clinic grows.
                                            </Typography>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, padding: '30px 20px', backgroundColor: '#ffffff', border: '1px solid #dce8f7', borderRadius: 16 }}>
                                                <Button aria-label="Remove clinician seat" onClick={() => setClinicSeats((seats) => Math.max(1, seats - 1))} disabled={clinicSeats === 1} style={{ minWidth: 42, minHeight: 42, borderRadius: 8, fontSize: 22, color: '#002D55' }}>−</Button>
                                                <div style={{ textAlign: 'center', minWidth: 130 }}>
                                                    <div style={{ color: '#002D55', fontWeight: 800, fontSize: 34, lineHeight: 1 }}>{clinicSeats}</div>
                                                    <Typography variant="caption" style={{ color: '#5a6b7d', fontWeight: 700 }}>{clinicSeats === 1 ? 'clinician seat' : 'clinician seats'}</Typography>
                                                </div>
                                                <Button aria-label="Add clinician seat" onClick={() => setClinicSeats((seats) => seats + 1)} style={{ minWidth: 42, minHeight: 42, borderRadius: 8, fontSize: 22, color: '#002D55' }}>+</Button>
                                            </div>
                                        </div>
                                    )}
                                    {savingsCalculatorStep === 1 && <>
                                    <Typography variant="body2" style={{ color: '#324158', marginBottom: 16 }}>
                                        Select the tools you use and adjust their monthly costs. Add anything else your clinic pays for below.
                                    </Typography>
                                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #dce8f7', borderRadius: 16, padding: 16, marginBottom: 18 }}>
                                        <Typography variant="body2" style={{ fontWeight: 700, color: '#002D55', marginBottom: 4 }}>
                                            Tools you likely pay for today
                                        </Typography>
                                        <Typography variant="caption" style={{ color: '#5a6b7d', display: 'block', marginBottom: 12 }}>
                                            These come pre-filled with typical monthly prices. Untick anything you don't use and edit the amounts to match your clinic.
                                        </Typography>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                                            {REPLACED_TOOLS.map((tool) => {
                                                const ToolIcon = tool.icon;
                                                const selection = toolSelections[tool.id] || { enabled: false, label: tool.label, cost: '' };
                                                return (
                                                    <div
                                                        key={tool.id}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 8,
                                                            padding: '10px 10px',
                                                            borderRadius: 12,
                                                            border: '1px solid ' + (selection.enabled ? '#FF7F27' : '#e0e6ee'),
                                                            backgroundColor: selection.enabled ? '#fff8f2' : '#f7f9fc',
                                                            opacity: selection.enabled ? 1 : 0.7,
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={selection.enabled}
                                                            onChange={() => handleToolToggle(tool.id)}
                                                            style={{ color: selection.enabled ? '#FF7F27' : '#94a3b8', padding: 4 }}
                                                        />
                                                        <ToolIcon style={{ color: selection.enabled ? '#FF7F27' : '#94a3b8', fontSize: 22, flexShrink: 0 }} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <TextField
                                                                value={selection.label || tool.label}
                                                                onChange={(e) => handleToolLabelChange(tool.id, e.target.value)}
                                                                variant="standard"
                                                                InputProps={{ disableUnderline: true, style: { color: '#002D55', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.2 } }}
                                                                fullWidth
                                                                inputProps={{ 'aria-label': `${tool.label} title` }}
                                                            />
                                                            {tool.range ? (
                                                                <Typography variant="caption" style={{ color: '#7b8794', display: 'block', lineHeight: 1.15, marginTop: 2 }}>
                                                                    {tool.range}
                                                                </Typography>
                                                            ) : null}
                                                            <TextField
                                                                value={selection.cost}
                                                                onChange={(e) => handleToolCostChange(tool.id, e.target.value)}
                                                                disabled={!selection.enabled}
                                                                variant="standard"
                                                                InputProps={{ disableUnderline: false, style: { fontSize: '0.8rem', color: '#324158' } }}
                                                                style={{ width: 90 }}
                                                                inputProps={{ inputMode: 'numeric', 'aria-label': `${tool.label} monthly cost in DKK` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {additionalTools.map((tool) => (
                                                <div
                                                    key={tool.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '10px 10px',
                                                        borderRadius: 12,
                                                        border: '1px solid ' + (tool.enabled ? '#FF7F27' : '#e0e6ee'),
                                                        backgroundColor: tool.enabled ? '#fff8f2' : '#f7f9fc',
                                                        opacity: tool.enabled ? 1 : 0.7,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={tool.enabled}
                                                        onChange={() => updateAdditionalTool(tool.id, 'enabled', !tool.enabled)}
                                                        style={{ color: tool.enabled ? '#FF7F27' : '#94a3b8', padding: 4 }}
                                                    />
                                                    <TuneIcon style={{ color: tool.enabled ? '#FF7F27' : '#94a3b8', fontSize: 22, flexShrink: 0 }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <TextField
                                                            value={tool.label}
                                                            onChange={(e) => updateAdditionalTool(tool.id, 'label', e.target.value)}
                                                            variant="standard"
                                                            InputProps={{ disableUnderline: true, style: { color: '#002D55', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.2 } }}
                                                            fullWidth
                                                            inputProps={{ 'aria-label': 'Additional custom expense title' }}
                                                        />
                                                        <TextField
                                                            value={tool.cost}
                                                            onChange={(e) => updateAdditionalTool(tool.id, 'cost', e.target.value)}
                                                            disabled={!tool.enabled}
                                                            variant="standard"
                                                            InputProps={{ disableUnderline: false, style: { fontSize: '0.8rem', color: '#324158' } }}
                                                            style={{ width: 90, marginTop: 2 }}
                                                            inputProps={{ inputMode: 'numeric', 'aria-label': 'Additional custom expense monthly cost in DKK' }}
                                                        />
                                                    </div>
                                                    <IconButton aria-label="Remove custom expense" onClick={() => setAdditionalTools((tools) => tools.filter((item) => item.id !== tool.id))} size="small" style={{ flexShrink: 0, color: '#94a3b8' }}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </div>
                                            ))}
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={addAdditionalTool}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        addAdditionalTool();
                                                    }
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 8,
                                                    minHeight: 74,
                                                    padding: '10px 10px',
                                                    borderRadius: 12,
                                                    border: '1px dashed #FF7F27',
                                                    backgroundColor: '#fff8f2',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                }}
                                            >
                                                <IconButton aria-label="Add another custom expense" style={{ color: '#FF7F27', padding: 4 }}>
                                                    <AddIcon style={{ fontSize: 34 }} />
                                                </IconButton>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10, marginTop: 12 }}>
                                            {customExpenses.map((expense) => (
                                                <div
                                                    key={expense.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '10px 10px',
                                                        minHeight: 74,
                                                        borderRadius: 12,
                                                        border: '1px solid #dce8f7',
                                                        backgroundColor: '#ffffff',
                                                    }}
                                                >
                                                    <TuneIcon style={{ color: '#FF7F27', fontSize: 22, flexShrink: 0 }} />
                                                    <TextField label="Other (add custom)" placeholder="Edit title" value={expense.name} onChange={(e) => updateCustomExpense(expense.id, 'name', e.target.value)} variant="outlined" size="small" style={{ flex: 1, minWidth: 0 }} />
                                                    <TextField label="DKK / mo" value={expense.cost} onChange={(e) => updateCustomExpense(expense.id, 'cost', e.target.value)} variant="outlined" size="small" style={{ width: 120, flexShrink: 0 }} inputProps={{ inputMode: 'numeric' }} />
                                                    <IconButton aria-label="Remove expense" onClick={() => removeCustomExpense(expense.id)} size="small" style={{ flexShrink: 0 }}><CloseIcon fontSize="small" /></IconButton>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '10px 10px', borderRadius: 12, border: '1px solid #dce8f7', backgroundColor: '#ffffff', marginTop: 12 }}>
                                            <div style={{ minWidth: 0 }}>
                                                <Typography variant="caption" style={{ color: '#7b8794', display: 'block', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                                    Estimated current spend
                                                </Typography>
                                                <div style={{ color: '#002D55', fontSize: 28, fontWeight: 900, lineHeight: 1.05, marginTop: 2 }}>
                                                    {formatDkkAmount(toolsMonthlyTotal + customExpensesTotal)} / mo
                                                </div>
                                                <Typography variant="caption" style={{ color: '#7b8794', display: 'block', marginTop: 4 }}>
                                                    Includes selected tools and custom expenses.
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginTop: 18 }}>
                                        {SAVINGS_CALCULATOR_TIERS.map((tier) => {
                                            const monthlyTierCost = tier.estimate * clinicSeats;
                                            const monthlyDifference = monthlySubscriptionSpend - monthlyTierCost;
                                            const annualDifference = monthlyDifference * 12;
                                            const hasMonthlySpend = monthlySubscriptionSpend > 0;
                                            const isSavings = monthlyDifference >= 0;

                                            return (
                                                <Paper
                                                    key={tier.id}
                                                    elevation={0}
                                                    style={{
                                                        borderRadius: 16,
                                                        border: '1px solid ' + (tier.id === 'tier2' ? '#FF7F27' : '#dce8f7'),
                                                        padding: 18,
                                                        backgroundColor: '#ffffff',
                                                    }}
                                                >
                                                    <div style={{ color: '#7b8794', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>
                                                        {tier.stage}
                                                    </div>
                                                    <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                                                        {tier.label}
                                                    </Typography>
                                                    <Typography variant="body2" style={{ color: '#5a6b7d', marginBottom: 10 }}>
                                                        {tier.summary}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#7b8794', display: 'block', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>
                                                        Virtus subscription
                                                    </Typography>
                                                    <div style={{ color: '#FF7F27', fontSize: 28, fontWeight: 900, lineHeight: 1.02 }}>
                                                        {formatDkkAmount(monthlyTierCost)}
                                                    </div>
                                                    <Typography variant="caption" style={{ color: '#7b8794', display: 'block', marginBottom: 14, fontSize: 12, fontWeight: 700 }}>
                                                        {tier.range} × {clinicSeats} {clinicSeats === 1 ? 'seat' : 'seats'}
                                                    </Typography>
                                                    {hasMonthlySpend ? (
                                                        <div style={{ borderTop: '1px solid #edf2f7', paddingTop: 12 }}>
                                                            <div style={{ borderRadius: 8, padding: '10px 12px', backgroundColor: isSavings ? '#f1f8f3' : '#fff4f3', border: '1px solid ' + (isSavings ? '#cce8d1' : '#f5d1ce') }}>
                                                            <Typography variant="caption" style={{ color: isSavings ? '#2e7d32' : '#c62828', display: 'block', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                                                {isSavings ? 'Estimated yearly savings' : 'Estimated yearly difference'}
                                                            </Typography>
                                                            <div style={{ color: isSavings ? '#2e7d32' : '#c62828', fontWeight: 900, fontSize: 28, lineHeight: 1.05, marginTop: 2 }}>
                                                                {isSavings
                                                                    ? formatDkkAmount(annualDifference)
                                                                    : formatDkkAmount(Math.abs(annualDifference))}
                                                            </div>
                                                            <Typography variant="body2" style={{ color: isSavings ? '#2e7d32' : '#c62828', marginTop: 4, fontWeight: 700 }}>
                                                                {isSavings
                                                                    ? `${formatDkkAmount(monthlyDifference)} less each month`
                                                                    : `${formatDkkAmount(Math.abs(monthlyDifference))} more each month`}
                                                            </Typography>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <Typography variant="body2" style={{ color: '#5a6b7d', borderTop: '1px solid #edf2f7', paddingTop: 12 }}>
                                                            Select an expense above to see the estimated difference.
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            );
                                        })}
                                    </div>
                                    </>}
                                </DialogContent>
                                <DialogActions style={{ padding: '8px 24px 16px', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" style={{ color: '#7b8794' }}>
                                        Estimates use the midpoint of each tier's current pricing range.
                                    </Typography>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {savingsCalculatorStep === 1 && <Button onClick={() => setSavingsCalculatorStep(0)} className={classes.signupButtonSecondary}>Back</Button>}
                                        {savingsCalculatorStep === 0
                                            ? <Button onClick={() => setSavingsCalculatorStep(1)} className={classes.signupButton} variant="contained">Next: expenses</Button>
                                            : <Button onClick={() => setSavingsCalculatorOpen(false)} className={classes.signupButtonSecondary}>Close calculator</Button>}
                                    </div>
                                </DialogActions>
                            </Dialog>
                                            <div className="clinicSectionDivider" />
                                            <h2 className="clinicSectionTitle">A journal that completes itself — driven by the patient, and your actions</h2>
                                            <p className="clinicSectionSubtitle">Patients complete a structured digital intake at home, in the waiting room, or before a video consultation. VIRTUS™ turns that into a ready-to-review clinical draft instead of another blank screen.</p>
                                        </div>
                                        <div className="clinicFeatureList">
                                            {INTAKE_FEATURES.map((item) => (
                                                <div key={item.title} className="clinicFeatureItem">
                                                    <div className="clinicFeatureCheck">
                                                        <CheckIcon style={{ fontSize: 18 }} />
                                                    </div>
                                                    <div>
                                                        <h3 className="clinicFeatureTitle">{item.title}</h3>
                                                        <p className="clinicFeatureBody">{item.body}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="clinicMockPanel">
                                        <div className="clinicMockCard">
                                            <div className="clinicMockRow">
                                                <span className="clinicMockLabel">Digital intake completed</span>
                                                <span className="clinicMockStatus clinicStatusOrange">Red flag detected</span>
                                            </div>
                                            <h3 className="clinicCardTitle">Mette Andersen, 44 — Low back pain</h3>
                                            <p className="clinicCardBody">Chief complaint, history, symptoms, PROM scores, and function limits are already structured before the consultation begins.</p>
                                        </div>
                                        <div className="clinicMockCard">
                                            <div className="clinicMockRow">
                                                <span className="clinicMockLabel">Auto-journal draft</span>
                                                <span className="clinicMockStatus clinicStatusBlue">Awaiting clinician review</span>
                                            </div>
                                            <h3 className="clinicCardTitle">History, PROMs, and flags pre-filled</h3>
                                            <p className="clinicCardBody">The clinician reviews, edits where needed, and signs instead of starting documentation from scratch.</p>
                                        </div>
                                        <div className="clinicMockCard">
                                            <div className="clinicMockRow">
                                                <span className="clinicMockLabel">Diagnosis workflow</span>
                                                <span className="clinicMockStatus clinicStatusGreen">Templates ready</span>
                                            </div>
                                            <h3 className="clinicCardTitle">Condition-linked exercise and follow-up logic</h3>
                                            <p className="clinicCardBody">Diagnosis selection can load the next-step workflow, education package, and follow-up structure instantly.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="clinicSection clinicSectionMuted">
                            <div className="clinicSectionContent">
                                <div className="clinicSectionHeader clinicExerciseHeader">
                                    <div className="clinicSectionEyebrow">Exercise delivery</div>
                                    <div className="clinicSectionDivider" />
                                    <h2 className="clinicSectionTitle">Adaptive exercise programs with clinician control built in</h2>
                                    <p className="clinicSectionSubtitle">Prescribe, monitor, and evolve home exercise pathways without giving up clinical oversight. The platform can adapt inside the rules you define, or stay fully manual.</p>
                                </div>
                                <div className="clinicValueGrid">
                                    {EXERCISE_CARDS.map((card) => {
                                        const ExerciseIcon = card.icon;

                                        return (
                                            <Paper key={card.title} elevation={0} className="clinicValueCard">
                                                <div className={`clinicValueIcon ${card.iconClassName}`}>
                                                    <ExerciseIcon style={{ fontSize: 22 }} />
                                                </div>
                                                <div className="clinicValueKicker">{card.kicker}</div>
                                                <h3 className="clinicValueTitle">{card.title}</h3>
                                                <p className="clinicValueBody">{card.body}</p>
                                            </Paper>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>

                        <section className="clinicSection">
                            <div className="clinicSectionContent">
                                <div className="clinicRemoteLayout">
                                    <div>
                                        <div className="clinicSectionHeader">
                                            <div className="clinicSectionEyebrow">Remote monitoring</div>
                                            <div className="clinicSectionDivider" />
                                            <h2 className="clinicSectionTitle">Stay close to every patient without increasing admin load</h2>
                                            <p className="clinicSectionSubtitle">The patient app keeps patients engaged between visits and feeds usable, structured data back into the clinician dashboard and journal.</p>
                                        </div>
                                        <div className="clinicFeatureList">
                                            {REMOTE_FEATURES.map((item) => (
                                                <div key={item.title} className="clinicFeatureItem">
                                                    <div className="clinicFeatureCheck">
                                                        <CheckIcon style={{ fontSize: 18 }} />
                                                    </div>
                                                    <div>
                                                        <h3 className="clinicFeatureTitle">{item.title}</h3>
                                                        <p className="clinicFeatureBody">{item.body}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="clinicMockPanel">
                                        <div className="clinicMockCard">
                                            <div className="clinicMockRow">
                                                <span className="clinicMockLabel">Patient monitoring — this week</span>
                                                <span className="clinicMockStatus clinicStatusGreen">12 active</span>
                                            </div>
                                            <h3 className="clinicCardTitle">Lars Nielsen — Knee, Phase 2</h3>
                                            <p className="clinicCardBody">Adherence, symptom trend, and satisfaction data are available before the follow-up consultation is even booked.</p>
                                        </div>
                                        <div className="clinicMockCard">
                                            <div className="clinicMockRow">
                                                <span className="clinicMockLabel">Follow-up note</span>
                                                <span className="clinicMockStatus clinicStatusBlue">Ready for review</span>
                                            </div>
                                            <h3 className="clinicCardTitle">Sara Holm — Visit tomorrow</h3>
                                            <p className="clinicCardBody">Remote check-in responses, PROM changes, and symptom commentary are already structured for the next visit.</p>
                                        </div>
                                        <div className="clinicMockCard">
                                            <div className="clinicMockRow">
                                                <span className="clinicMockLabel">Patient message</span>
                                                <span className="clinicMockStatus clinicStatusOrange">2 minutes ago</span>
                                            </div>
                                            <h3 className="clinicCardTitle">Mikkel R. — “Knee feels sore after session 3”</h3>
                                            <p className="clinicCardBody">Respond, review the journal context, and adjust the program without leaving the patient record.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/*<section className="clinicSection clinicSectionMuted">
                            <div className="clinicSectionContent">
                                <div className="clinicSectionHeader">
                                    <div className="clinicSectionEyebrow">Built for modern clinics</div>
                                    <div className="clinicSectionDivider" />
                                    <h2 className="clinicSectionTitle">Keep the existing imagery, but reorganise it around the new story</h2>
                                    <p className="clinicSectionSubtitle">The current portfolio visuals now support the new landing-page structure instead of sitting in a long linear benefits list.</p>
                                </div>
                                <div className="clinicModernLayout">
                                    <div className="clinicShowcasePanel">
                                        <div className="clinicShowcaseFrame">
                                            {selectedShowcase.mediaType === 'video' && !videoLoadFailed ? (
                                                <video
                                                    key={selectedOverlay}
                                                    className="clinicShowcaseVideo"
                                                    poster={selectedShowcase.poster || showcaseLaptop}
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                    controls={false}
                                                    onError={() => setVideoLoadFailed(true)}
                                                >
                                                    <source src={SHOWCASE_VIDEO_SRC} type="video/mp4" />
                                                </video>
                                            ) : selectedShowcase.mediaType === 'video' ? (
                                                <div className="clinicShowcaseUnavailable">
                                                    <img className="clinicShowcaseImage" src={selectedShowcase.poster || showcaseLaptop} alt={selectedShowcase.title} />
                                                    <div className="clinicShowcaseUnavailableNote">Autoplay video slot is wired. Add a local demo asset at public/media/clinic-demo.mp4 to activate this showcase view.</div>
                                                </div>
                                            ) : (
                                                <img className="clinicShowcaseImage" src={selectedShowcase.image} alt={selectedShowcase.title} />
                                            )}
                                        </div>
                                        <div className="clinicChipRow">
                                            {SHOWCASE_CHIPS.map((chip) => (
                                                <button
                                                    key={chip}
                                                    type="button"
                                                    className={`clinicChipButton${chip === selectedOverlay ? ' active' : ''}`}
                                                    onClick={() => handleShowcaseSelect(chip)}
                                                >
                                                    {chip}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="clinicShowcaseMeta">
                                            <div className="clinicEyebrow">{selectedShowcase.eyebrow}</div>
                                            <div className="clinicShowcaseTitle">{selectedShowcase.title}</div>
                                            <div className="clinicShowcaseText">{selectedShowcase.text}</div>
                                        </div>
                                    </div>

                                    <div className="clinicStoryGrid">
                                        {STORY_CARDS.map((card) => (
                                            <Paper key={card.title} elevation={0} className="clinicStoryCard">
                                                <div className="clinicStoryImageWrap">
                                                    <img className="clinicStoryImage" src={card.image} alt={card.title} />
                                                </div>
                                                <div className="clinicStoryBody">
                                                    <h3 className="clinicCardTitle">{card.title}</h3>
                                                    <p className="clinicCardBody">{card.body}</p>
                                                </div>
                                            </Paper>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>*/}

                        <section className="clinicSection clinicSectionMuted">
                            <div className="clinicSectionContent">
                                <div className="clinicSectionHeader">
                                    <div className="clinicSectionEyebrow">Video consultations</div>
                                    <div className="clinicSectionDivider" />
                                </div>
                                <div className="clinicVideoHighlight">
                                    <div>
                                        <h2 className="clinicSectionTitle" style={{ color: '#fff', marginTop: 0 }}>Secure video consultations that fit the rest of the workflow</h2>
                                        <p className="clinicSectionSubtitle" style={{ color: 'rgba(255,255,255,0.74)' }}>Run video visits directly inside VIRTUS™ Clinic as a standalone telehealth appointment or as one part of a hybrid care pathway.</p> {/* The chip-selected media showcase above is wired to autoplay a local demo video for this feature. */}
                                        <ul className="clinicVideoList">
                                            <li><CheckIcon style={{ fontSize: 18, color: '#81c784', marginTop: 2 }} /><span>Patients can complete intake before joining the virtual waiting room.</span></li>
                                            <li><CheckIcon style={{ fontSize: 18, color: '#81c784', marginTop: 2 }} /><span>Journal context, PROMs, and red flags stay visible during the visit.</span></li>
                                            <li><CheckIcon style={{ fontSize: 18, color: '#81c784', marginTop: 2 }} /><span>Standalone video appointment types can live beside in-clinic bookings.</span></li>
                                            <li><CheckIcon style={{ fontSize: 18, color: '#81c784', marginTop: 2 }} /><span>Hybrid care becomes a workflow choice, not a separate product stack.</span></li>
                                        </ul>
                                    </div>
                                    <div className="clinicVideoPanel">
                                        <div className="clinicVideoScreen">
                                            <div className="clinicVideoPlayButton">▶</div>
                                        </div>
                                        <div className="clinicVideoControls">
                                            <span className="clinicVideoLiveDot" />
                                            <span className="clinicVideoControlLabel">Live consultation · 14:32</span>
                                            <div className="clinicVideoActions">
                                                <button type="button" className="clinicVideoAction">Journal</button>
                                                <button type="button" className="clinicVideoAction">PROM</button>
                                                <button type="button" className="clinicVideoAction">End</button>
                                            </div>
                                        </div>
                                        <div className="clinicVideoSubcard">
                                            <div className="clinicMockLabel" style={{ color: 'rgba(255,255,255,0.5)' }}>Concurrent journal view</div>
                                            <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>Intake summary, PROM scores, and red flags remain visible alongside the video call. Document in real time and sign at the end.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="clinicSection">
                            <div className="clinicSectionContent">
                                <div className="clinicSectionHeader">
                                    <div className="clinicSectionEyebrow">Clinical admin and EHR</div>
                                    <div className="clinicSectionDivider" />
                                    <h2 className="clinicSectionTitle">A complete clinic operating system when you are ready for it</h2>
                                    <p className="clinicSectionSubtitle">From booking to billing and from intake to epicrisis, the platform can scale from workflow layer to full clinic OS without forcing an all-at-once switch.</p>
                                </div>
                                <div className="clinicValueGrid">
                                    {ADMIN_CARDS.map((card) => {
                                        const AdminIcon = card.icon;

                                        return (
                                            <Paper key={card.title} elevation={0} className="clinicValueCard">
                                                <div className={`clinicValueIcon ${card.iconClassName}`}>
                                                    <AdminIcon style={{ fontSize: 22 }} />
                                                </div>
                                                <div className="clinicValueKicker">{card.kicker}</div>
                                                <h3 className="clinicValueTitle">{card.title}</h3>
                                                <p className="clinicValueBody">{card.body}</p>
                                            </Paper>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>

                        <section className="clinicSection">
                            <div className="clinicSectionContent">
                                <div className="clinicAddonBanner">
                                    <div>
                                        <span className="clinicAddonPill">Optional add-on</span>
                                        <h2 className="clinicSectionSubtitle">Ambient scribe support for clinics that want a lighter documentation load</h2>
                                        <p className="clinicAddonText">Position ambient scribe support as an optional add-on for consultations where real-time structured transcription is useful. It should complement the VIRTUS™ workflow and still keep clinician review in the loop.</p>
                                    </div>
                                    <div className="clinicAddonMark">Corti</div>
                                </div>
                            </div>
                        </section>

                        <section className="clinicPlansSection">
                            <div className="clinicPlansContent">
                                <div className="clinicSectionHeader" >
                                    <div className="clinicSectionEyebrow">Flexible plans</div>
                                    <div className="clinicSectionDivider" />
                                    <h2 className="clinicSectionTitle">Start free with digital intake or a full clinic system at a competitive cost</h2>
                                    <p className="clinicSectionSubtitle">Start free with digital intake and pay per qualified lead or choose a full clinic system that replaces 2-4 different subscriptions at a lower cost. Find the plan that best fits your clinic's needs.</p>
                                </div>
                                <div className="clinicPlansGrid">
                                    {SIGNUP_PLANS.map((plan) => (
                                        <Paper key={plan.id} elevation={0} className={`clinicPlanCard${plan.id === 'stage1' ? ' featured' : ''}`}>
                                            {plan.badge ? <span className="clinicPlanBadge">{plan.badge}</span> : null}
                                            <div className="clinicPlanStage">{plan.stage}</div>
                                            <h3 className="clinicPlanTitle">{plan.tagline}</h3>
                                            <p className="clinicPlanTagline">{plan.id === 'stage0' ? 'Make your clinic available for patients to match with you as the right physio and receive patients who book through the platform with pre-written journal draft.' : plan.id === 'stage1' ? 'Smart journaling and patient-management layer.' : 'Automate bookkeeping and accounting with Appelsin integration. Unlock EAN billing, MedCom integration, staff admin, patient analytics, and full clinic management features.'}</p>
                                            <div className="clinicPlanPrice">
                                                <span className="clinicPlanAmount">{plan.price}</span>
                                                <span>{plan.priceUnit}</span>
                                            </div>
                                            <ul className="clinicPlanFeatures">
                                                {plan.features.map((feature) => (
                                                    <li key={feature}>
                                                        <CheckIcon style={{ fontSize: 18, color: '#81c784', marginTop: 2 }} />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Paper>
                                    ))}
                                </div>
                            </div>
                        </section>

                        <section className="clinicSavingsTeaser">
                            <div className="clinicSavingsTeaserInner">
                                <div className="clinicSavingsTeaserCopy">
                                    <div className="clinicSavingsTeaserIcon"><ReceiptIcon style={{ fontSize: 25 }} /></div>
                                    <div>
                                        <div className="clinicSavingsTeaserEyebrow">Your savings estimate</div>
                                        <h2 className="clinicSavingsTeaserTitle">See what your clinic could save</h2>
                                        <p className="clinicSavingsTeaserText">Compare the subscriptions you use today with one connected clinical platform.</p>
                                    </div>
                                </div>
                                <Button variant="contained" disableElevation className="clinicPrimaryButton" onClick={() => { setSavingsCalculatorStep(0); setSavingsCalculatorOpen(true); }}>
                                    Calculate savings
                                </Button>
                            </div>
                        </section>

                        <section className="clinicSection clinicFaqSection">
                            <div className="clinicSectionContent" style={{ maxWidth: 840 }}>
                                <div className="clinicSectionHeader" style={{ textAlign: 'center', margin: '0 auto 18px' }}>
                                    <div className="clinicSectionEyebrow">Common questions</div>
                                    <h2 className="clinicSectionTitle">Frequently asked questions</h2>
                                </div>
                                <div className="clinicFaqList">
                                    {faqs.map((faq, idx) => (
                                        <div key={faq.q} className="clinicFaqItem">
                                            <Button
                                                className="clinicFaqButton"
                                                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                                aria-expanded={openFaq === idx}
                                                aria-controls={`faq-panel-${idx}`}
                                            >
                                                <span>{faq.q}</span>
                                                <span>{openFaq === idx ? '−' : '+'}</span>
                                            </Button>
                                            <Collapse in={openFaq === idx} timeout="auto" unmountOnExit>
                                                <div id={`faq-panel-${idx}`} className="clinicFaqAnswer">{faq.a}</div>
                                            </Collapse>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        <section className="clinicFinalCta">
                            <div className="clinicFinalContent">
                                <h2 className="clinicFinalTitle">Ready to run a smarter clinic?</h2>
                                <p className="clinicFinalText">Use VIRTUS™ Clinic to streamline intake, reduce documentation friction, and deliver better continuity of care across in-person and remote pathways.</p>
                                <div className="clinicButtonRow" style={{ justifyContent: 'center', marginBottom: 0 }}>
                                    <Button variant="contained" disableElevation className="clinicPrimaryButton" onClick={openSignupDialog}>
                                        Sign-up to our BETA test for free {/*Get started today*/}
                                    </Button>
                                    <Button variant="contained" disableElevation className="clinicSecondaryButton" href="#contact">
                                        Book a demo
                                    </Button>
                                </div>
                            </div>
                        </section>
                    </div>
        </Grid>
    );
}

export default withStyles(styles)(ClinicContent);
